import React, { useState } from 'react';
import { Crown, ExternalLink, Mic, Disc, Video, Trophy } from 'lucide-react';
import { Dialog, DialogContent, DialogTrigger, DialogTitle, DialogDescription } from '../components/ui/dialog';

// Card FOUNDER con teaser hover Netflix-style
const FounderCard = ({ founder }) => {
  const [isHovering, setIsHovering] = useState(false);
  const teaserId = founder.teaserVideoId;

  return (
    <Dialog>
      <DialogTrigger asChild>
        <div
          className="rounded-lg overflow-hidden border-2 border-yellow-600 hover:border-yellow-400 transition-all duration-300 cursor-pointer group"
          style={{ backgroundColor: 'var(--tp-dark)' }}
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
          data-testid={`founder-card-${founder.id}`}
        >
          <div className="h-48 sm:h-56 relative overflow-hidden">
            <img src={founder.image} alt={founder.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
            {isHovering && teaserId && (
              <iframe
                src={`https://www.youtube.com/embed/${teaserId}?autoplay=1&mute=1&loop=1&controls=0&modestbranding=1&rel=0&playsinline=1&playlist=${teaserId}&start=10`}
                title={`${founder.name} - preview`}
                data-testid={`founder-card-teaser-${founder.id}`}
                className="absolute inset-0 w-full h-full pointer-events-none"
                style={{ border: 0 }}
                allow="autoplay; encrypted-media"
                referrerPolicy="strict-origin-when-cross-origin"
                aria-hidden="true"
                tabIndex={-1}
              />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent pointer-events-none" />
            <div className="absolute top-3 right-3 px-2 py-1 rounded-full bg-yellow-600 pointer-events-none">
              <Crown size={14} className="text-black" />
            </div>
            <div className="absolute bottom-3 left-3 right-3 pointer-events-none">
              <h3 className="text-xl sm:text-2xl font-bold text-white mb-1" style={{ fontFamily: 'Bebas Neue, sans-serif', letterSpacing: '1px' }}>
                {founder.name}
              </h3>
              <p className="text-yellow-400 text-xs font-bold mb-1" style={{ fontFamily: 'Oswald, sans-serif' }}>
                {founder.realName}
              </p>
              <p className="text-gray-300 text-xs">{founder.role}</p>
            </div>
          </div>
          <div className="p-3 sm:p-4">
            <p className="text-gray-400 text-xs sm:text-sm">{founder.shortBio}</p>
          </div>
        </div>
      </DialogTrigger>
      <DialogContent className="bg-gray-900 border-gray-700 max-w-[95vw] sm:max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogTitle className="sr-only">{founder.name}</DialogTitle>
        <DialogDescription className="sr-only">{founder.shortBio}</DialogDescription>
        <div className="space-y-4">
          <div className="relative h-48 sm:h-64 rounded-lg overflow-hidden">
            <img src={founder.image} alt={founder.name} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
            <div className="absolute bottom-4 left-4 right-4">
              <div className="flex items-center gap-2 mb-2">
                <Crown size={20} className="text-yellow-600" />
                <span className="text-yellow-600 font-bold text-xs" style={{ fontFamily: 'Oswald, sans-serif' }}>FOUNDER</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-1" style={{ fontFamily: 'Bebas Neue, sans-serif', letterSpacing: '1px' }}>
                {founder.name}
              </h2>
              <p className="text-yellow-400 text-sm font-bold" style={{ fontFamily: 'Oswald, sans-serif' }}>
                {founder.realName}
              </p>
            </div>
          </div>

          <div className="px-2 space-y-2 text-sm sm:text-base">
            {founder.fullBio}
          </div>

          {/* Video embed (YouTube) */}
          {founder.videoId && (
            <div className="mt-6 rounded-lg overflow-hidden border border-gray-700" style={{ backgroundColor: 'rgba(0,0,0,0.4)' }}>
              <div className="px-4 py-3 border-b border-gray-700">
                <h5 className="text-base font-bold text-yellow-500" style={{ fontFamily: 'Oswald, sans-serif' }}>
                  {founder.videoLabel || 'GUARDA IL SET'}
                </h5>
              </div>
              <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                <iframe
                  src={`https://www.youtube.com/embed/${founder.videoId}`}
                  title={`${founder.name} - Video`}
                  data-testid={`founder-video-${founder.id}`}
                  className="absolute top-0 left-0 w-full h-full"
                  style={{ border: 0 }}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                />
              </div>
            </div>
          )}

          {/* Playlist Spotify */}
          {founder.spotifyPlaylistId && (
            <div className="mt-4 rounded-xl overflow-hidden border border-gray-700" style={{ backgroundColor: 'rgba(0,0,0,0.4)' }}>
              <div className="px-4 py-3 border-b border-gray-700 flex items-center gap-2">
                <svg viewBox="0 0 168 168" className="w-4 h-4 fill-[#1DB954]" aria-hidden="true">
                  <path d="M83.996.277C37.747.277.253 37.77.253 84.019c0 46.251 37.494 83.741 83.743 83.741 46.254 0 83.744-37.49 83.744-83.741 0-46.246-37.49-83.738-83.745-83.738zm38.404 120.78a5.217 5.217 0 01-7.18 1.73c-19.662-12.01-44.414-14.73-73.564-8.07a5.222 5.222 0 01-6.249-3.93 5.213 5.213 0 013.926-6.25c31.9-7.29 59.263-4.15 81.337 9.34 2.46 1.51 3.24 4.72 1.73 7.18zm10.25-22.805c-1.89 3.075-5.91 4.045-8.98 2.155-22.51-13.839-56.823-17.846-83.448-9.764-3.453 1.043-7.1-.903-8.148-4.35-1.04-3.453.907-7.093 4.354-8.143 30.413-9.228 68.222-4.758 94.072 11.127 3.07 1.89 4.04 5.91 2.15 8.976v-.001zm.88-23.744c-26.99-16.031-71.52-17.505-97.289-9.684-4.138 1.255-8.514-1.081-9.768-5.219a7.835 7.835 0 015.221-9.771c29.581-8.98 78.756-7.245 109.83 11.202a7.833 7.833 0 012.74 10.733c-2.2 3.722-7.02 4.949-10.73 2.739z"/>
                </svg>
                <h5 className="text-base font-bold text-[#1DB954]" style={{ fontFamily: 'Oswald, sans-serif' }}>
                  ASCOLTA LA PLAYLIST SPOTIFY
                </h5>
              </div>
              <iframe
                src={`https://open.spotify.com/embed/playlist/${founder.spotifyPlaylistId}?utm_source=generator`}
                title={`${founder.name} - Playlist`}
                data-testid={`founder-spotify-${founder.id}`}
                width="100%"
                height="352"
                style={{ border: 0, borderRadius: '0 0 12px 12px' }}
                allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                loading="lazy"
                allowFullScreen
              />
            </div>
          )}

          <div className="flex flex-wrap gap-3 pt-4 border-t border-gray-700">
            {founder.links.map((link, idx) => (
              <a
                key={idx}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2 rounded bg-yellow-600 hover:bg-yellow-700 text-black font-bold text-sm flex items-center gap-2 transition-colors"
                style={{ fontFamily: 'Oswald, sans-serif' }}
              >
                {link.label}
                <ExternalLink size={14} />
              </a>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Card MODALITÀ: supporta teaser hover Netflix-style (YouTube o Vimeo)
const SectionCard = ({ section }) => {
  const [isHovering, setIsHovering] = useState(false);
  const ytId = section.teaserVideoId;
  const vimeoId = section.teaserVimeoId;

  return (
    <Dialog>
      <DialogTrigger asChild>
        <div
          className="rounded-lg overflow-hidden border border-gray-800 hover:border-red-600 transition-all duration-300 cursor-pointer group"
          style={{ backgroundColor: 'var(--tp-dark)' }}
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
          data-testid={`section-card-${section.id}`}
        >
          <div className="h-40 sm:h-48 relative overflow-hidden" style={{ backgroundColor: section.imageFit === 'contain' ? '#dc2626' : undefined }}>
            <img
              src={section.image}
              alt={section.title}
              className={`w-full h-full ${section.imageFit === 'contain' ? 'object-contain' : 'object-cover'} group-hover:scale-105 transition-transform duration-500`}
              style={section.imagePosition ? { objectPosition: section.imagePosition } : {}}
            />
            {isHovering && ytId && (
              <iframe
                src={`https://www.youtube.com/embed/${ytId}?autoplay=1&mute=1&loop=1&controls=0&modestbranding=1&rel=0&playsinline=1&playlist=${ytId}`}
                title={`${section.title} - preview`}
                data-testid={`section-card-teaser-${section.id}`}
                className="absolute inset-0 w-full h-full pointer-events-none"
                style={{ border: 0 }}
                allow="autoplay; encrypted-media"
                referrerPolicy="strict-origin-when-cross-origin"
                aria-hidden="true"
                tabIndex={-1}
              />
            )}
            {isHovering && vimeoId && (
              <iframe
                src={`https://player.vimeo.com/video/${vimeoId}?autoplay=1&muted=1&loop=1&background=1&controls=0`}
                title={`${section.title} - preview`}
                data-testid={`section-card-teaser-${section.id}`}
                className="absolute inset-0 w-full h-full pointer-events-none"
                style={{ border: 0 }}
                allow="autoplay; fullscreen"
                referrerPolicy="strict-origin-when-cross-origin"
                aria-hidden="true"
                tabIndex={-1}
              />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent pointer-events-none" />
            <h3 className="absolute bottom-3 left-3 right-3 text-base sm:text-lg font-bold text-white" style={{ fontFamily: 'Oswald, sans-serif' }}>
              {section.title}
            </h3>
          </div>
          <div className="p-3 sm:p-4">
            <p className="text-gray-400 text-xs sm:text-sm">{section.shortDesc}</p>
          </div>
        </div>
      </DialogTrigger>
      <DialogContent className="bg-gray-900 border-gray-700 max-w-[95vw] sm:max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogTitle className="sr-only">{section.title}</DialogTitle>
        <DialogDescription className="sr-only">{section.shortDesc}</DialogDescription>
        <div className="space-y-4">
          <div className="relative h-48 sm:h-56 rounded-lg overflow-hidden">
            <img
              src={section.image}
              alt={section.title}
              className="w-full h-full object-cover"
              style={section.imagePosition ? { objectPosition: section.imagePosition } : {}}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
            <div className="absolute bottom-4 left-4 right-4">
              <h2 className="text-3xl sm:text-4xl font-bold text-white" style={{ fontFamily: 'Bebas Neue, sans-serif', letterSpacing: '1px' }}>
                {section.title}
              </h2>
              <p className="text-gray-300 text-sm mt-1">{section.shortDesc}</p>
            </div>
          </div>

          <div className="px-2 space-y-2 text-sm sm:text-base">
            {section.fullContent}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Card DJ UFFICIALE con teaser hover Netflix-style
const DJCard = ({ dj }) => {
  const [isHovering, setIsHovering] = useState(false);
  const teaserId = dj.teaserVideoId || dj.videoId;

  return (
    <Dialog>
      <DialogTrigger asChild>
        <div
          className="rounded-lg overflow-hidden border-2 border-red-600 hover:border-red-400 transition-all duration-300 cursor-pointer group"
          style={{ backgroundColor: 'var(--tp-dark)' }}
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
          data-testid={`dj-card-${dj.id}`}
        >
          <div className="h-48 sm:h-56 relative overflow-hidden">
            <img
              src={dj.image}
              alt={dj.name}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              style={{ objectPosition: 'center 25%' }}
            />
            {isHovering && teaserId && (
              <iframe
                src={`https://www.youtube.com/embed/${teaserId}?autoplay=1&mute=1&loop=1&controls=0&modestbranding=1&rel=0&playsinline=1&playlist=${teaserId}&start=10`}
                title={`${dj.name} - preview`}
                data-testid={`dj-card-teaser-${dj.id}`}
                className="absolute inset-0 w-full h-full pointer-events-none"
                style={{ border: 0 }}
                allow="autoplay; encrypted-media"
                referrerPolicy="strict-origin-when-cross-origin"
                aria-hidden="true"
                tabIndex={-1}
              />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent pointer-events-none" />
            <div className="absolute top-3 right-3 px-2 py-1 rounded-full bg-red-600 pointer-events-none">
              <Disc size={14} className="text-white" />
            </div>
            <div className="absolute bottom-3 left-3 right-3 pointer-events-none">
              <h3 className="text-xl sm:text-2xl font-bold text-white mb-1" style={{ fontFamily: 'Bebas Neue, sans-serif', letterSpacing: '1px' }}>
                {dj.name}
              </h3>
              {dj.realName && dj.realName !== dj.name && (
                <p className="text-red-400 text-xs font-bold mb-1" style={{ fontFamily: 'Oswald, sans-serif' }}>
                  {dj.realName}
                </p>
              )}
              <p className="text-gray-300 text-xs">{dj.role}</p>
            </div>
          </div>
          <div className="p-3 sm:p-4">
            <p className="text-gray-400 text-xs sm:text-sm">{dj.shortBio}</p>
          </div>
        </div>
      </DialogTrigger>
      <DialogContent className="bg-gray-900 border-gray-700 max-w-[95vw] sm:max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogTitle className="sr-only">{dj.name}</DialogTitle>
        <DialogDescription className="sr-only">DJ ufficiale Tecniche Perfette</DialogDescription>
        <div className="space-y-4">
          <div className="relative h-56 sm:h-72 rounded-lg overflow-hidden">
            <img
              src={dj.image}
              alt={dj.name}
              className="w-full h-full object-cover"
              style={{ objectPosition: 'center 25%' }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/85 to-transparent" />
            <div className="absolute bottom-4 left-4 right-4">
              <div className="flex items-center gap-2 mb-2">
                <Disc size={20} className="text-red-500" />
                <span className="text-red-500 font-bold text-xs" style={{ fontFamily: 'Oswald, sans-serif' }}>DJ UFFICIALE</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-1" style={{ fontFamily: 'Bebas Neue, sans-serif', letterSpacing: '1px' }}>
                {dj.name}
              </h2>
              {dj.realName && dj.realName !== dj.name && (
                <p className="text-red-400 text-sm font-bold" style={{ fontFamily: 'Oswald, sans-serif' }}>
                  {dj.realName}
                </p>
              )}
            </div>
          </div>

          <div className="px-2 space-y-2 text-sm sm:text-base">
            {dj.fullBio}
          </div>

          {dj.videoId && (
            <div className="mt-6 rounded-lg overflow-hidden border border-gray-700" style={{ backgroundColor: 'rgba(0,0,0,0.4)' }}>
              <div className="px-4 py-3 border-b border-gray-700 flex items-center justify-between">
                <h5 className="text-base font-bold text-red-400" style={{ fontFamily: 'Oswald, sans-serif' }}>
                  {dj.videoLabel || 'GUARDA IL SET'}
                </h5>
                {dj.videoCredit && (
                  <span className="text-[11px] text-gray-400" style={{ fontFamily: 'Oswald, sans-serif' }}>
                    Credit: {dj.videoCredit}
                  </span>
                )}
              </div>
              <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                <iframe
                  src={`https://www.youtube.com/embed/${dj.videoId}`}
                  title={`${dj.name} - Video`}
                  data-testid={`dj-video-${dj.id}`}
                  className="absolute top-0 left-0 w-full h-full"
                  style={{ border: 0 }}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                />
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Card CONTENT CREATOR con teaser hover Netflix-style
const CreatorCard = ({ creator }) => {
  const [isHovering, setIsHovering] = useState(false);
  const teaserId = creator.teaserVideoId || creator.videoId;

  return (
    <Dialog>
      <DialogTrigger asChild>
        <div
          className="rounded-lg overflow-hidden border-2 border-purple-600 hover:border-purple-400 transition-all duration-300 cursor-pointer group"
          style={{ backgroundColor: 'var(--tp-dark)' }}
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
          data-testid={`creator-card-${creator.id}`}
        >
          <div className="h-48 sm:h-56 relative overflow-hidden">
            <img
              src={creator.image}
              alt={creator.name}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              style={{ objectPosition: creator.imagePosition || 'center 25%' }}
            />
            {isHovering && teaserId && (
              <iframe
                src={`https://www.youtube.com/embed/${teaserId}?autoplay=1&mute=1&loop=1&controls=0&modestbranding=1&rel=0&playsinline=1&playlist=${teaserId}&start=10`}
                title={`${creator.name} - preview`}
                data-testid={`creator-card-teaser-${creator.id}`}
                className="absolute inset-0 w-full h-full pointer-events-none"
                style={{ border: 0 }}
                allow="autoplay; encrypted-media"
                referrerPolicy="strict-origin-when-cross-origin"
                aria-hidden="true"
                tabIndex={-1}
              />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent pointer-events-none" />
            <div className="absolute top-3 right-3 px-2 py-1 rounded-full bg-purple-600 pointer-events-none">
              <Video size={14} className="text-white" />
            </div>
            <div className="absolute bottom-3 left-3 right-3 pointer-events-none">
              <h3 className="text-xl sm:text-2xl font-bold text-white mb-1" style={{ fontFamily: 'Bebas Neue, sans-serif', letterSpacing: '1px' }}>
                {creator.name}
              </h3>
              {creator.realName && creator.realName !== creator.name && (
                <p className="text-purple-300 text-xs font-bold mb-1" style={{ fontFamily: 'Oswald, sans-serif' }}>
                  {creator.realName}
                </p>
              )}
              <p className="text-gray-300 text-xs">{creator.role}</p>
            </div>
          </div>
          <div className="p-3 sm:p-4">
            <p className="text-gray-400 text-xs sm:text-sm">{creator.shortBio}</p>
          </div>
        </div>
      </DialogTrigger>
      <DialogContent className="bg-gray-900 border-gray-700 max-w-[95vw] sm:max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogTitle className="sr-only">{creator.name}</DialogTitle>
        <DialogDescription className="sr-only">Content Creator</DialogDescription>
        <div className="space-y-4">
          <div className="relative h-56 sm:h-72 rounded-lg overflow-hidden">
            <img
              src={creator.image}
              alt={creator.name}
              className="w-full h-full object-cover"
              style={{ objectPosition: creator.imagePosition || 'center 25%' }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/85 to-transparent" />
            <div className="absolute bottom-4 left-4 right-4">
              <div className="flex items-center gap-2 mb-2">
                <Video size={20} className="text-purple-400" />
                <span className="text-purple-400 font-bold text-xs" style={{ fontFamily: 'Oswald, sans-serif' }}>CONTENT CREATOR</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-1" style={{ fontFamily: 'Bebas Neue, sans-serif', letterSpacing: '1px' }}>
                {creator.name}
              </h2>
              {creator.realName && creator.realName !== creator.name && (
                <p className="text-purple-300 text-sm font-bold" style={{ fontFamily: 'Oswald, sans-serif' }}>
                  {creator.realName}
                </p>
              )}
            </div>
          </div>

          <div className="px-2 space-y-2 text-sm sm:text-base">
            {creator.fullBio}
          </div>

          {creator.videoId && (
            <div className="mt-6 rounded-lg overflow-hidden border border-gray-700" style={{ backgroundColor: 'rgba(0,0,0,0.4)' }}>
              <div className="px-4 py-3 border-b border-gray-700">
                <h5 className="text-base font-bold text-purple-400" style={{ fontFamily: 'Oswald, sans-serif' }}>
                  {creator.videoLabel || 'GUARDA IL VIDEO'}
                </h5>
              </div>
              <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                <iframe
                  src={`https://www.youtube.com/embed/${creator.videoId}`}
                  title={`${creator.name} - Video`}
                  data-testid={`creator-video-${creator.id}`}
                  className="absolute top-0 left-0 w-full h-full"
                  style={{ border: 0 }}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                />
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const BiografiePage = () => {
  // Founders data
  const founders = [
    {
      id: 'mastafive',
      name: 'MASTAFIVE',
      realName: 'Johnny Mastrocinque',
      image: 'https://customer-assets.emergentagent.com/job_site-mirror-165/artifacts/27itdca2_e93d0f_22c98461621a4ec1935a1062e37be4a1~mv2.avif',
      role: 'DJ, Producer, CEO',
      shortBio: 'Co-fondatore nel 2003 del contest Tecniche Perfette, precursore di spicco della scena Hip Hop Italiana.',
      teaserVideoId: '0dY8ggQlo9M',
      videoId: '0dY8ggQlo9M',
      videoLabel: 'GUARDA IL SET',
      spotifyPlaylistId: '37i9dQZF1DZ06evO37znfc',
      fullBio: (
        <>
          <p className="text-gray-300 mb-4 leading-relaxed">
            Mastafive al secolo <strong className="text-white">Johnny Mastrocinque</strong>, 30 anni sul mixer, sia come Dee jay che come produttore, predilige le sonorità Funkie Jazz, Rap, Rock in cui la sua poliedricità artistica si riversa in pista.
          </p>
          <p className="text-gray-300 mb-4 leading-relaxed">
            Cresciuto in seno alla cultura Hip Hop ne è uno dei testimoni più appassionati.
          </p>
          <p className="text-gray-300 mb-4 leading-relaxed">
            Esponente e produttore insieme a Deal (Mauro Bottoni) della leggendaria Crew Rap italiana di fine anni 90: <strong className="text-white">Gate Keepaz</strong>.
          </p>
          <p className="text-gray-300 mb-4 leading-relaxed">
            Ideatore e organizzatore della sfida a colpi di rime <strong className="text-red-400">"Tecniche Perfette"</strong> (www.tecnicheperfette.com).
          </p>
          <p className="text-gray-300 mb-4 leading-relaxed">
            Fondatore e presidente della label <strong className="text-white">B.M.Records</strong> (www.bmrecords.eu) e CEO di <strong className="text-white">Massivo Label</strong>. È stato autore consulente e giudice del programma culto di MTV "SPIT".
          </p>
          <p className="text-gray-300 mb-4 leading-relaxed">
            Per tre edizioni giudice speciale per le sfide dirette della trasmissione <strong className="text-white">AMICI</strong>.
          </p>
          <p className="text-gray-300 mb-4 leading-relaxed">
            Ha collaborato come direttore artistico della label "Tempi Duri" fondata da Fabri Fibra.
          </p>
          <p className="text-gray-300 mb-4 leading-relaxed">
            Direzione artistica e produzione di eventi dal 2009 ad oggi con <strong className="text-white">MAS Network</strong>.
          </p>
          <p className="text-gray-300 mb-4 leading-relaxed">
            Proprietario del locale storico e di riferimento <strong className="text-white">"JMB"</strong> nella provincia di Torino.
          </p>
          <p className="text-gray-300 mb-4 leading-relaxed">
            Autore e collaboratore dal 2013 di <strong className="text-white">Radio DEE JAY</strong> (One Two - Occupy DEEJAY - Hotline Bars - CYPHAKINGS - SAY WAAAD).
          </p>
          <p className="text-gray-300 mb-4 leading-relaxed">
            Fondatore di <strong className="text-white">Radio For Music</strong> e collaboratore di <strong className="text-white">Hot Block Radio</strong>.
          </p>
          <p className="text-gray-300 mb-4 leading-relaxed">
            Produttore poliedrico presente anche in colonne sonore di serie tv e film.
          </p>
          <p className="text-gray-300 leading-relaxed">
            Ad oggi attraversa la penisola con i suoi set e live dove accompagna moltissimi artisti nazionali sul palco, non c'è limite al suono di Mastafive.
          </p>
        </>
      ),
      links: [
        { label: 'SITO UFFICIALE', url: 'https://www.mastafive.com' },
        { label: 'INSTAGRAM', url: 'https://www.instagram.com/mastafive' }
      ]
    },
    {
      id: 'djdoubles',
      name: 'DJ DOUBLE S',
      realName: 'Rino Costante',
      image: 'https://customer-assets.emergentagent.com/job_site-mirror-165/artifacts/kx8qyvcm_Ebbgfb-XkAAkaeR.png',
      role: 'DJ, Turntablist',
      shortBio: 'Co-fondatore di Tecniche Perfette, fanatico del vinile e Mixtape King fin dagli anni \'90.',
      teaserVideoId: 'ZToSpE3rhxQ',
      videoId: 'ZToSpE3rhxQ',
      videoLabel: 'GUARDA IL SET',
      fullBio: (
        <>
          <p className="text-gray-300 mb-4 leading-relaxed">
            <strong className="text-white">Torino</strong> è la città di provenienza. <strong className="text-white">Il Sud</strong> le origini. Una carriera quasi ventennale.
          </p>
          <p className="text-gray-300 mb-4 leading-relaxed">
            Ha condiviso il palco con i più grandi della scena Hip Hop italiana e ha aperto concerti di molti artisti d'oltreoceano.
          </p>
          <p className="text-gray-300 mb-4 leading-relaxed">
            Fanatico e appassionato di <strong className="text-white">vinile</strong>. <strong className="text-red-400">Mixtape King</strong>, fin dagli anni '90.
          </p>
          <p className="text-gray-300 mb-4 leading-relaxed">
            Una vita in tour per la penisola tra <strong className="text-white">Jam, Live e DJ Set</strong>.
          </p>
          <p className="text-gray-300 mb-4 leading-relaxed">
            <strong className="text-white">Tecnica, talento, stile e conoscenza.</strong>
          </p>
          <p className="text-gray-300 leading-relaxed">
            Questo ed altro ancora è <strong className="text-red-400">DJ DOUBLE S</strong>.
          </p>
        </>
      ),
      links: [
        { label: 'SITO UFFICIALE', url: 'https://www.djdoubles.com' }
      ]
    },
    {
      id: 'dragwan',
      name: 'DRAGWAN',
      realName: 'Salvatore Calandra (Sal Calandra)',
      image: 'https://customer-assets.emergentagent.com/job_site-mirror-165/artifacts/1zexv84f_WhatsApp%20Image%202026-04-15%20at%2021.33.40.jpeg',
      role: 'MC',
      shortBio: 'Ideatore della JAM che prese il nome di Tecniche Perfette, MC membro della storica crew GATE KEEPAZ',
      fullBio: (
        <>
          <p className="text-gray-300 mb-4 leading-relaxed">
            <strong className="text-white">DragWan</strong>, all'anagrafe <strong className="text-white">Salvatore Calandra</strong> (noto anche come Sal Calandra), è una figura storica della cultura Hip Hop italiana. Appassionato dal 1995, è attivo ufficialmente sulla scena dal 1999.
          </p>
          <p className="text-gray-300 mb-4 leading-relaxed">
            Durante il suo percorso ha esplorato a fondo le varie discipline, dal <strong className="text-white">writing</strong> al <strong className="text-white">beatbox</strong>, dal <strong className="text-white">b-boying</strong> al <strong className="text-white">rapping</strong>, scegliendo infine quest'ultima forma di espressione come mezzo principale per forgiare la propria identità.
          </p>
          <h4 className="text-xl font-bold mt-6 mb-3 text-red-400" style={{ fontFamily: 'Oswald, sans-serif' }}>La Nascita di Tecniche Perfette</h4>
          <p className="text-gray-300 mb-4 leading-relaxed">
            Un capitolo fondamentale e iconico della sua storia riguarda la nascita di <strong className="text-red-400">Tecniche Perfette</strong>. Quella che oggi è l'istituzione del freestyle italiano, nasce originariamente come una <strong className="text-white">Jam</strong> ideata proprio da DragWan e alcuni suoi amici.
          </p>
          <p className="text-gray-300 mb-4 leading-relaxed">
            L'obiettivo era organizzare una Jam "tributo" dedicata ai <strong className="text-white">Gate Keepaz</strong> (che all'epoca erano il loro gruppo preferito), celebrando tutti e quattro gli elementi dell'Hip Hop e battezzando l'evento proprio con il titolo del brano più famoso della crew.
          </p>
          <p className="text-gray-300 mb-4 leading-relaxed">
            Per gestire la parte dedicata all'MCing, DragWan chiese a <strong className="text-white">Mastafive</strong> di inventarsi qualcosa di speciale. Mastafive, che da tempo teneva nel cassetto l'idea di scovare il miglior MC da battle della nazione, colse la palla al balzo: unendo le forze in quell'occasione unica, prese vita la <strong className="text-red-400">leggendaria battle nazionale di freestyle</strong>.
          </p>
          <p className="text-gray-300 leading-relaxed">
            Ex membro della <strong className="text-white">Universal Zulu Nation Italia</strong>, DragWan è oggi orgogliosamente membro proprio della storica <strong className="text-white">Gate Keepaz Crew</strong> che tanto lo aveva ispirato agli esordi.
          </p>
        </>
      ),
      links: []
    },
    {
      id: 'walterix',
      name: 'WALTERIX',
      realName: 'Walter Marello',
      image: 'https://customer-assets.emergentagent.com/job_site-mirror-165/artifacts/4a53gxz6_498922986_24105672065685245_1475086043899522059_n.jpg',
      role: 'DJ, Producer, Host',
      shortBio: 'Primo host ufficiale del Tecniche Perfette, DJ e produttore storico della scena torinese.',
      fullBio: (
        <>
          <p className="text-gray-300 mb-4 leading-relaxed">
            <strong className="text-white">Walter Marello</strong>, in arte <strong className="text-red-400">Walterix</strong>, è un noto DJ, produttore e promotore della scena hip hop italiana, originario di <strong className="text-white">Vinovo (Torino)</strong>.
          </p>
          <p className="text-gray-300 mb-4 leading-relaxed">
            Figura cardine del panorama nazionale, è ricordato per essere stato il <strong className="text-white">primo host ufficiale del "Tecniche Perfette"</strong>. Ha guidato le battaglie del celebre contest di freestyle fino ai leggendari ed epici spareggi tra <strong className="text-white">Ensi</strong> e <strong className="text-white">Mondo Marcio</strong>, culminati con la vittoria di Ensi dopo il quinto e definitivo round.
          </p>
          <h4 className="text-xl font-bold mt-6 mb-3 text-red-400" style={{ fontFamily: 'Oswald, sans-serif' }}>Percorso Artistico</h4>
          <p className="text-gray-300 mb-4 leading-relaxed">
            La sua carriera prende il via nei primi anni '90, alimentata dalla profonda passione per la cultura del <strong className="text-white">vinile</strong> e per il rap americano.
          </p>
          <p className="text-gray-300 mb-4 leading-relaxed">
            <strong className="text-white">Gli esordi:</strong> Insieme al socio Mauris, inizia a farsi le ossa producendo le prime musicassette mixate sotto il nome di <strong className="text-white">"Dj Walterix in the mix"</strong>.
          </p>
          <p className="text-gray-300 mb-4 leading-relaxed">
            <strong className="text-white">L'affermazione:</strong> Consolida il suo nome e si fa conoscere a livello nazionale grazie alla collaborazione con i <strong className="text-white">Gate Keepaz</strong>, la storica e influente crew di Nichelino.
          </p>
          <p className="text-gray-300 mb-4 leading-relaxed">
            <strong className="text-white">Collaborazioni storiche:</strong> Per quasi un decennio è stato una colonna portante dal vivo per il rapper <strong className="text-white">Maury B</strong>, accompagnandolo sui palchi di tutta Italia al fianco di Dj Taglierino. Creando con lui quella che sarebbe diventata per 5 edizioni la matrice ispiratrice di grandi JAM sul Freestyle il <strong className="text-red-400">"MIC CHECK"</strong>, evento da cui sono passati ed anche "nati" il 90% dei protagonisti della scena anni 90 dell'Hip Hop Italiano.
          </p>
          <h4 className="text-xl font-bold mt-6 mb-3 text-red-400" style={{ fontFamily: 'Oswald, sans-serif' }}>Progetti e Collettivi</h4>
          <p className="text-gray-300 mb-4 leading-relaxed">
            <strong className="text-white">Dynamite Soul Men:</strong> Verso la fine degli anni '90, fonda questo acclamato collettivo insieme a Dj Taglierino. Il duo diventa rapidamente celebre per l'originalità e le altissime abilità tecniche al giradischi. La prova più emblematica del loro talento è il mixtape <strong className="text-white">"Dinamite Soul 6"</strong>, un'impresa epica in cui riuscirono a mixare ben <strong className="text-red-400">500 dischi in soli 100 minuti</strong>.
          </p>
          <p className="text-gray-300 mb-4 leading-relaxed">
            <strong className="text-white">Walterix Promotion:</strong> In tempi più recenti, ha saputo reinventarsi come creatore digitale e divulgatore. Attraverso la piattaforma <strong className="text-white">Original Hip Hop Entertainment / Walterix Promotion</strong>, continua instancabilmente a diffondere la cultura della doppia H tramite format video, approfondimenti e progetti tematici come la <strong className="text-red-400">"Dissing Saga"</strong>.
          </p>
          <p className="text-gray-300 leading-relaxed">
            Oggi, Walterix è universalmente considerato una <strong className="text-white">figura di riferimento assoluta</strong> per chiunque cerchi l'autenticità delle radici hip hop torinesi, distinguendosi da sempre per uno stile inconfondibile e folle che unisce il puro intrattenimento a una personale tecnica di mixing in vinile.
          </p>
        </>
      ),
      links: []
    }
  ];

  // Host Ufficiali
  const hosts = [
    {
      id: 'casco',
      name: 'CASCO',
      realName: 'Caschetto Helmet',
      image: 'https://customer-assets.emergentagent.com/job_site-mirror-165/artifacts/4980j1q1_Casco%20bono.png',
      role: 'MC, Freestyler, Host',
      shortBio: 'MC e freestyler underground, presenza costante nelle jam. Legato ai Vikings e al Muretto di Milano.',
      fullBio: (
        <>
          <p className="text-gray-300 mb-4 leading-relaxed">
            <strong className="text-white">Casco</strong>, aka <strong className="text-white">Caschetto Helmet</strong>, è uno di quei nomi che non trovi spinti dalle major ma che, se mastichi freestyle in Italia, ti sono passati davanti più volte di quanto vorresti ammettere. MC, freestyler e presenza costante nelle jam, è legato a due realtà chiave della scena underground: i <strong className="text-white">Vikings</strong> e il leggendario <strong className="text-white">Muretto di Milano</strong>.
          </p>
          <p className="text-gray-300 mb-4 leading-relaxed">
            Cresciuto dentro la cultura battle, Casco si è costruito una <strong className="text-white">reputazione sul campo</strong>, non su Instagram. Il suo stile è diretto, sporco quando serve, tecnico senza fare il professorino. Non è quello che fa la punchline "carina" per piacere a tutti, è quello che ti entra addosso quando sale in cypher. Tradotto: o lo capisci, o ti travolge.
          </p>
          <p className="text-gray-300 mb-4 leading-relaxed">
            Al <strong className="text-red-400">Muretto di Milano</strong>, che per anni è stato una specie di università non ufficiale del freestyle italiano, Casco ha affinato presenza scenica e risposta immediata. Lì non hai tempo di pensarci troppo: <strong className="text-white">o spacchi o sparisci</strong>. E lui è uno di quelli rimasti.
          </p>
          <p className="text-gray-300 leading-relaxed">
            Con i <strong className="text-white">Vikings</strong>, crew conosciuta nell'ambiente battle, ha consolidato il suo percorso, partecipando a eventi, contest e situazioni live dove la credibilità conta più dei follower. Zero costruzioni artificiali: chi lo conosce sa perché è lì.
          </p>
        </>
      ),
      links: []
    },
    {
      id: 'ares-adami',
      name: 'ARES ADAMI',
      realName: 'Ares Adami',
      image: 'https://customer-assets.emergentagent.com/job_site-mirror-165/artifacts/dgrkynk7_Ares_Adami-banner-goldworld.jpg',
      role: 'MC, Host, Organizer',
      shortBio: 'Caposaldo del freestyle italiano. 3 finali TP (2004-2006), vincitore Master of Battle Arena 2014. Host di Tecniche Perfette.',
      fullBio: (
        <>
          <p className="text-gray-300 mb-4 leading-relaxed">
            Nato ad <strong className="text-white">Arco (TN)</strong>, <strong className="text-white">Ares Adami</strong> è un caposaldo del freestyle e dell'Hip Hop italiano. Membro della <strong className="text-white">Bangbass crew</strong>, ha segnato la scena con <strong className="text-red-400">tre finali nazionali al Tecniche Perfette</strong> (2004-2006) e la vittoria al <strong className="text-red-400">Master of Battle Arena 2014</strong>.
          </p>
          <p className="text-gray-300 mb-4 leading-relaxed">
            Mentore di talenti come <strong className="text-white">Drimer</strong>, Ares spinge la cultura anche come organizzatore e host. Creatore dello storico contest <strong className="text-red-400">"Natale Letale"</strong>, oggi conduce i palchi dei più prestigiosi eventi nazionali.
          </p>
          <h4 className="text-xl font-bold mt-6 mb-3 text-red-400" style={{ fontFamily: 'Oswald, sans-serif' }}>Eventi Condotti</h4>
          <ul className="text-gray-300 space-y-2 list-disc list-inside">
            <li><strong className="text-white">Tecniche Perfette</strong></li>
            <li><strong className="text-white">Still Here</strong></li>
            <li><strong className="text-white">Carpe Diem</strong></li>
            <li><strong className="text-white">Mic Tyson</strong></li>
            <li><strong className="text-white">RedBull Frista</strong></li>
          </ul>
        </>
      ),
      links: []
    },
    {
      id: 'emis-killa',
      name: 'EMIS KILLA',
      realName: 'Emiliano Giambelli',
      image: 'https://customer-assets.emergentagent.com/job_site-mirror-165/artifacts/tp5pjk44_Emis-Killa-2023-e1683542346494.jpg',
      role: 'MC, Rapper, Host',
      shortBio: 'Campione italiano Tecniche Perfette 2007. Pilastro del nuovo rap italiano con successi multiplatino.',
      fullBio: (
        <>
          <p className="text-gray-300 mb-4 leading-relaxed">
            <strong className="text-white">Emis Killa</strong>, pseudonimo di <strong className="text-white">Emiliano Giambelli</strong>, è uno dei pilastri del nuovo rap italiano. Nato a <strong className="text-white">Vimercate</strong> nel <strong className="text-white">1989</strong>, si è imposto sulla scena grazie a un <strong className="text-red-400">flow tecnico</strong> e a un'attitudine street autentica, collezionando successi multiplatino da <em>L'Erba Cattiva</em> a <em>Terza Stagione</em>.
          </p>
          <p className="text-gray-300 mb-4 leading-relaxed">
            Le sue radici affondano nel <strong className="text-red-400">freestyle</strong>: nel <strong className="text-white">2007</strong> vinse il titolo di <strong className="text-red-400">campione italiano al Tecniche Perfette</strong>. Legatissimo alla manifestazione ha partecipato a quasi tutte le edizioni successive come <strong className="text-white">giuria</strong>, ne è diventato poi un <strong className="text-red-400">volto ufficiale</strong>, presentando la finalissima insieme a <strong className="text-white">Mastafive</strong> nel <strong className="text-white">2022</strong> e nel <strong className="text-white">2023</strong>.
          </p>
        </>
      ),
      links: []
    },
    {
      id: 'kiave',
      name: 'MIRKO KIAVE',
      realName: 'Mirko Kiave',
      image: 'https://customer-assets.emergentagent.com/job_site-mirror-165/artifacts/tci6bp18_653811148_10166995041726515_2521894558537010192_n.jpg',
      role: 'MC, Rapper, Host',
      shortBio: 'Rapper cosentino classe \'81, colonna dell\'underground italiano. Host del contest Tecniche Perfette per tutto il 2019.',
      fullBio: (
        <>
          <p className="text-gray-300 mb-4 leading-relaxed">
            Rapper cosentino classe '81, <strong className="text-white">Kiave</strong> è una colonna dell'underground italiano. Dai collettivi <strong className="text-white">Dietro Le Quinte</strong> e <strong className="text-white">Blue-Nox</strong> alla finale di <strong className="text-red-400">MTV Spit</strong>, ha ridefinito il freestyle e lo storytelling con album come <strong className="text-white">7 Respiri</strong> e <strong className="text-white">StereoTelling</strong>.
          </p>
          <p className="text-gray-300 mb-4 leading-relaxed">
            Oltre alla musica, usa il rap come <strong className="text-red-400">impegno sociale</strong>, curando laboratori nelle carceri e progetti contro le discriminazioni.
          </p>
          <p className="text-gray-300 leading-relaxed">
            Coerente e tecnico, è stato <strong className="text-red-400">Host del contest Tecniche Perfette per tutto il 2019</strong>.
          </p>
        </>
      ),
      links: []
    },
    {
      id: 'joelz',
      name: 'JOELZ',
      realName: 'Gioele Vella',
      image: 'https://customer-assets.emergentagent.com/job_site-mirror-165/artifacts/cfq9yq2e_82182701_2709063005803678_8814102992606199808_n.jpg',
      role: 'MC, Rapper, Host',
      shortBio: 'Rapper classe \'94, fratello di Ensi e Raige. Roster Juicy Music, dal 2018 affianca Mastafive in alcune tappe di TP.',
      fullBio: (
        <>
          <p className="text-gray-300 mb-4 leading-relaxed">
            <strong className="text-white">Gioele Vella</strong>, conosciuto nella scena rap con lo pseudonimo di <strong className="text-white">Joelz</strong>, è un rapper e paroliere italiano classe 1994. Nato e cresciuto in provincia di Torino, il rap per lui è letteralmente un affare di famiglia: è infatti il fratello minore di due pesi massimi della scena hip hop italiana, <strong className="text-white">Ensi</strong> (Jari Ivan Vella) e <strong className="text-white">Raige</strong> (Alex Vella), fondatori del leggendario gruppo <strong className="text-white">OneMic</strong>.
          </p>
          <p className="text-gray-300 mb-4 leading-relaxed">
            Nonostante la pesante eredità del cognome, Joelz ha saputo ritagliarsi il proprio spazio costruendo un'identità artistica personale, caratterizzata da una penna riflessiva e da un'attitudine fresca che unisce il rispetto per le radici underground a sonorità più attuali.
          </p>
          <p className="text-gray-300 mb-4 leading-relaxed">
            È la punta di diamante del roster di <strong className="text-white">Juicy Music</strong>, la factory creativa ed etichetta indipendente fondata proprio dal fratello Ensi. Negli ultimi anni ha trascorso anche un periodo a Genova, inserendosi e collaborando attivamente anche con la scena underground del capoluogo ligure (come testimoniano le collaborazioni con DJ Kamo e altri artisti locali).
          </p>
          <p className="text-gray-300 mb-4 leading-relaxed">
            Il suo sodalizio musicale più forte è quello con il producer <strong className="text-white">Dave_Zeta</strong>, con il quale ha sviluppato la gran parte del suo immaginario sonoro, sfociato nel 2024 nel progetto discografico <strong className="text-white">Day Off</strong>.
          </p>
          <h4 className="text-xl font-bold mt-6 mb-3 text-red-400" style={{ fontFamily: 'Oswald, sans-serif' }}>Tracce & Collaborazioni Rilevanti</h4>
          <ul className="text-gray-300 space-y-2 mb-4">
            <li className="leading-relaxed">
              <strong className="text-white">"Formula" (2024):</strong> Un brano dal forte valore simbolico, una vera e propria family reunion in cui Joelz divide per la prima volta ufficialmente la traccia con i fratelli maggiori Raige ed Ensi.
            </li>
            <li className="leading-relaxed">
              <strong className="text-white">"Verde" (2024):</strong> Singolo di spicco estratto da Day Off, che vanta le collaborazioni di Willie Peyote e Cicco Sanchez.
            </li>
            <li className="leading-relaxed">
              <strong className="text-white">"Caffarel" (2024):</strong> Posse track con Santiago KeiKei, Michael Sorriso, Thai Smoke e Dj Dops.
            </li>
            <li className="leading-relaxed">
              <strong className="text-white">"Un colpo alla volta" (2023):</strong> Singolo prodotto da DJ Kamo, in cui Joelz collabora con Albe OK e Spike.
            </li>
            <li className="leading-relaxed">
              <strong className="text-white">"Giassai" (2026):</strong> Recente collaborazione nel progetto discografico dell'artista Adriana.
            </li>
          </ul>
          <p className="text-gray-300 mb-4 leading-relaxed">
            Giovane, ma con un solido background, Joelz continua a portare avanti con onore e rime affilate la "dinastia" dei Vella nel rap italiano.
          </p>
          <p className="text-gray-300 leading-relaxed">
            Dal <strong className="text-red-400">2018</strong> è saltuariamente l'<strong className="text-red-400">Host che affianca Mastafive in alcune tappe e finale di Tecniche Perfette</strong>.
          </p>
        </>
      ),
      links: []
    },
    {
      id: 'rido-mc',
      name: 'RIDO MC',
      realName: 'Rido MC',
      image: 'https://customer-assets.emergentagent.com/job_site-mirror-165/artifacts/9dh9pcn9_Rido-MC.jpg',
      role: 'MC, Host, Organizer',
      shortBio: 'Dalla seconda edizione per una decade host ufficiale TP. Rapper dal 1992, organizzatore The Show Off, redattore Groove e AELLE MAG.',
      fullBio: (
        <>
          <p className="text-gray-300 mb-4 leading-relaxed">
            Dalla seconda edizione e per una decade è stato più volte <strong className="text-red-400">host ufficiale e coadiuvante di Mastafive</strong>, <strong className="text-white">Rido MC</strong> ha cominciato a rappare nel <strong className="text-white">1992</strong>, come modo di espressione diffusosi nella periferia di Milano. Dal 1997 fino ad oggi ha pubblicato un album, un EP ed ha collaborato con svariati artisti italiani e stranieri.
          </p>
          <p className="text-gray-300 mb-4 leading-relaxed">
            Dal 2002 è organizzatore delle serate <strong className="text-white">The Show Off</strong>, tra le più conosciute del genere hip hop non solo a Milano. Oltre alla carriera musicale, Rido è stato redattore della rivista <strong className="text-white">Groove</strong>, dove cura sia la parte musicale che quella di lifestyle. A questo aggiunge la scrittura e la conduzione del programma musicale <strong className="text-white">Rapture</strong>, in onda per sette stagioni su All Music.
          </p>
          
          <h4 className="text-xl font-bold mt-6 mb-3 text-red-400" style={{ fontFamily: 'Oswald, sans-serif' }}>Gli Inizi: Cricca Dei Balordi</h4>
          <p className="text-gray-300 mb-4 leading-relaxed">
            La sua carriera artistica inizia in corrispondenza di quella della <strong className="text-white">Cricca Dei Balordi</strong>, nata a Stresa nel 1992 con il nome di Cracka Posse dapprima attivi nei live e come freestyler nella zona del varesotto, poi con contatti nella ben più conosciuta zona del milanese. Il primo lavoro ufficiale compare sul mixtape <strong className="text-white">Sanobusiness V.M.18</strong> di Bassi Maestro, insieme allo stesso ed ai Sottotono, per poi partecipare successivamente all'album omonimo di Fritz da Cat con il brano <em>De Stijl</em>. Nel 1996 incidono il demo <strong className="text-white">Fondazione Cracka</strong>, prodotto da DJ Double T, Fritz da Cat e Bassi Maestro.
          </p>
          
          <h4 className="text-xl font-bold mt-6 mb-3 text-red-400" style={{ fontFamily: 'Oswald, sans-serif' }}>Sano Business & Discografia</h4>
          <p className="text-gray-300 mb-4 leading-relaxed">
            Il primo lavoro ufficiale come <strong className="text-white">Sano Business</strong> è nel 1997 su <em>The Micragnous EP</em> con il brano <em>Gioco di ruolo</em> insieme a Marya, a cui fanno seguito diverse partecipazioni a mixtape sia di Bassi Maestro che del beatmaker DJ Zeta. Come CDB sono ancora sulle tracce per le due compilation <strong className="text-white">50 Mc's</strong>, <em>Lo capisci l'italiano</em> di DJ Double S e su tutti gli album di Bassi Maestro.
          </p>
          <p className="text-gray-300 mb-4 leading-relaxed">
            Dopo l'uscita nel 1999 del singolo su vinile <em>Ninja Rap/L'album dei balordi</em>, tra il 2000 e il 2002 escono i due album <strong className="text-white">Ninja Rap</strong> e <strong className="text-white">Musi</strong>. Inoltre facendo parte del collettivo dei Sano Business insieme a Bassi Maestro, Supa e DJ Zeta ha partecipato ai due album <strong className="text-white">Esuberanza</strong> e <strong className="text-white">Crossover</strong>.
          </p>
          
          <h4 className="text-xl font-bold mt-6 mb-3 text-red-400" style={{ fontFamily: 'Oswald, sans-serif' }}>Collaborazioni & Progetti Recenti</h4>
          <p className="text-gray-300 mb-4 leading-relaxed">
            Rido ha inoltre realizzato svariate collaborazioni come artista solista, come ad esempio nei due mixtape di Bassi Maestro <em>Monkee Bizniz Vol. 2</em> (2006) e <em>Monkee Bizniz Vol. 3</em> (2007). Nel 2009 ha collaborato con <strong className="text-white">Gué Pequeno</strong> per la presentazione del mixtape <em>Fastlife Mixtape Vol. 2 - Faster Life</em>.
          </p>
          <p className="text-gray-300 leading-relaxed">
            Redattore e volto della nuova versione di <strong className="text-white">AELLE MAG</strong> su YouTube dal 2024 è ora fuori con un nuovo lavoro firmato <strong className="text-red-400">CDB</strong> con il socio di sempre <strong className="text-white">SUPA</strong>.
          </p>
        </>
      ),
      links: []
    }
  ];

  // DJ Ufficiali
  const djs = [
    {
      id: 'dj-taglierino',
      name: "DJ TAGLIERINO",
      realName: "Gianluca Taglierino aka Lil' Cut",
      image: 'https://customer-assets.emergentagent.com/job_site-mirror-165/artifacts/exjy1ykg_487828281_9761133787266043_4359652485492925690_n.jpg',
      role: 'DJ, Sound Engineer, Producer',
      shortBio: "DJ ufficiale di Tecniche Perfette e artista del roster BMRECORDS. Proprietario del Soul 23 Studio.",
      videoId: 'yewgq7XahL4',
      videoCredit: 'Torino Underground',
      videoLabel: 'GUARDA IL SET',
      fullBio: (
        <>
          <p className="text-gray-300 mb-4 leading-relaxed">
            <strong className="text-white">Gianluca Taglierino</strong> (in arte <strong className="text-white">DJ Taglierino</strong> o <strong className="text-white">DJ Lil' Cut</strong>) è il DJ ufficiale di <strong className="text-red-400">Tecniche Perfette</strong> e un artista del roster <strong className="text-white">BMRECORDS</strong>.
          </p>
          <p className="text-gray-300 mb-4 leading-relaxed">
            Nato a Torino il <strong className="text-white">9 dicembre 1978</strong> ma originario di <strong className="text-white">Vercelli</strong>, Gianluca Taglierino è uno dei disc jockey, ingegneri del suono e produttori più solidi e rispettati del panorama hip hop e underground italiano.
          </p>

          <h4 className="text-xl font-bold mt-6 mb-3 text-red-400" style={{ fontFamily: 'Oswald, sans-serif' }}>Le Origini e i Giradischi</h4>
          <p className="text-gray-300 mb-4 leading-relaxed">
            Il suo viaggio nella musica inizia molto presto. Incuriosito dall'impianto stereo del padre, a soli <strong className="text-white">13 anni</strong> mette le mani sui primi dischi. La scoperta del djing si trasforma rapidamente in una vocazione: Gianluca affina in tempi record le tecniche di mixaggio e scratching, portando il suo suono fuori dalla sua cameretta e dentro la scena urban italiana.
          </p>

          <h4 className="text-xl font-bold mt-6 mb-3 text-red-400" style={{ fontFamily: 'Oswald, sans-serif' }}>Il Cuore dell'Hip Hop: Tecniche Perfette e BMRECORDS</h4>
          <p className="text-gray-300 mb-4 leading-relaxed">
            Ad oggi, la sua figura è indissolubilmente legata alle istituzioni del rap in Italia:
          </p>
          <ul className="text-gray-300 space-y-3 list-disc list-inside mb-4">
            <li className="leading-relaxed">
              <strong className="text-white">DJ Ufficiale di Tecniche Perfette:</strong> da anni è la colonna portante musicale della battle di freestyle più famosa e longeva d'Italia. Dietro la consolle accompagna colpi di rime ed extrabeat, gestendo i beat su cui si misurano i migliori MC della nazione durante le finalissime e le tappe regionali.
            </li>
            <li className="leading-relaxed">
              <strong className="text-white">Artista e Sound Engineer BMRECORDS:</strong> all'interno della label e hub creativo indipendente fondato da Mastafive, Gianluca è un pilastro assoluto. Non opera solo come DJ e beatmaker, ma mette a disposizione dell'etichetta la sua maestria tecnica in fase di registrazione, mixaggio e masterizzazione.
            </li>
          </ul>

          <h4 className="text-xl font-bold mt-6 mb-3 text-red-400" style={{ fontFamily: 'Oswald, sans-serif' }}>Studio di Registrazione e Collaborazioni</h4>
          <p className="text-gray-300 mb-4 leading-relaxed">
            L'impatto di DJ Taglierino non si limita alle esibizioni dal vivo. Dietro le quinte, è un <strong className="text-white">"architetto" del suono</strong>:
          </p>
          <ul className="text-gray-300 space-y-3 list-disc list-inside mb-4">
            <li className="leading-relaxed">
              <strong className="text-white">Soul 23 Studio:</strong> è proprietario e fondatore del <strong className="text-red-400">Soul 23</strong>, rinomato studio di registrazione situato nel Nord Italia, vera e propria fucina per dischi, mixtape e progetti della scena.
            </li>
            <li className="leading-relaxed">
              <strong className="text-white">Live e Tour:</strong> nel corso della sua carriera ha condiviso il palco e lo studio con giganti del rap nostrano. È stato, tra le altre cose, il DJ ufficiale di <strong className="text-white">Raige</strong> (Warner Music) e ha preso parte a collettivi storici e progetti di spessore come <strong className="text-white">Vecchi Bastardi</strong> (con ATPC, Lefty, ecc.) e svariate produzioni firmate Mastafive/Gatekeepaz.
            </li>
            <li className="leading-relaxed">
              <strong className="text-white">Didattica:</strong> unendo la vocazione per il suono alla volontà di tramandare le sue competenze, Gianluca è anche un Trainer specializzato per l'utilizzo dei software di produzione musicale <strong className="text-white">Ableton Live</strong> e <strong className="text-white">Logic Pro</strong>.
            </li>
          </ul>

          <h4 className="text-xl font-bold mt-6 mb-3 text-red-400" style={{ fontFamily: 'Oswald, sans-serif' }}>La Visione</h4>
          <p className="text-gray-300 leading-relaxed">
            Oggi, nel 2026, Gianluca DJ Taglierino continua a rappresentare l'<strong className="text-white">anello di congiunzione perfetto tra la vecchia scuola analogica del vinile e le moderne tecniche di ingegneria del suono</strong>. Che stia infiammando una jam o scolpendo i suoni in studio, rimane una garanzia assoluta di stile, cultura e dedizione alla musica.
          </p>
        </>
      ),
      links: []
    },
    {
      id: 'dj-shocca',
      name: "DJ SHOCCA",
      realName: "Matteo Bernacchi (aka Roc B)",
      image: 'https://customer-assets.emergentagent.com/job_site-mirror-165/artifacts/e5hmdmw5_Shocca.jpg',
      role: 'DJ, Beatmaker, Producer',
      shortBio: "DJ Ufficiale di varie selezioni TP, beatmaker leggendario e fondatore di Unlimited Struggle.",
      videoId: 'edLIPbwyJSk',
      videoLabel: 'GUARDA IL VIDEO',
      fullBio: (
        <>
          <p className="text-gray-300 mb-4 leading-relaxed">
            <strong className="text-white">DJ Shocca</strong> è stato numerose volte fin dai primi anni <strong className="text-red-400">DJ Ufficiale di alcune selezioni del Tecniche Perfette</strong>, è un artista, produttore e fondatore dello storico collettivo e asse portante dell'hip hop italiano <strong className="text-white">Unlimited Struggle</strong>.
          </p>
          <p className="text-gray-400 text-sm mb-6 italic">
            Di seguito la biografia completa aggiornata ad aprile 2026.
          </p>

          <h4 className="text-xl font-bold mt-2 mb-3 text-red-400" style={{ fontFamily: 'Oswald, sans-serif' }}>Biografia</h4>
          <p className="text-gray-300 mb-4 leading-relaxed">
            Nato a <strong className="text-white">Treviso il 28 marzo 1979</strong> e conosciuto anche con l'alter ego di <strong className="text-white">Roc B</strong>, DJ Shocca è unanimemente considerato uno dei più grandi e influenti beatmaker e DJ della storia dell'hip hop in Italia. Il suo suono — caratterizzato da <strong className="text-white">campionamenti soul e funk, batterie corpose e un inconfondibile sapore "golden age"</strong> — ha fatto scuola, definendo l'estetica del rap underground italiano per oltre due decenni.
          </p>

          <h4 className="text-xl font-bold mt-6 mb-3 text-red-400" style={{ fontFamily: 'Oswald, sans-serif' }}>Le Origini e i Primi Passi</h4>
          <p className="text-gray-300 mb-4 leading-relaxed">
            Il percorso musicale di Matteo inizia alla fine degli anni '90. Nel <strong className="text-white">1997</strong> forma il gruppo <strong className="text-white">Centro 13</strong> insieme a <strong className="text-white">Frank Siciliano</strong> e altri artisti della scena veneta, debuttando nel <strong className="text-white">1999</strong> con l'album <em>Acciaio</em>. Fin dai primissimi lavori emerge il suo talento cristallino nel costruire strumentali dal sapore classico ma dall'impatto devastante, che lo portano a collaborare con i nomi più caldi dell'underground dell'epoca.
          </p>

          <h4 className="text-xl font-bold mt-6 mb-3 text-red-400" style={{ fontFamily: 'Oswald, sans-serif' }}>Il Capolavoro: "60 Hz" e la Nascita di un Classico</h4>
          <p className="text-gray-300 mb-4 leading-relaxed">
            Il vero punto di svolta arriva nel <strong className="text-white">2004</strong> con la pubblicazione di <strong className="text-red-400">"60 Hz"</strong>. Distribuito da <strong className="text-white">Vibrarecords</strong>, l'album è un vero e proprio monumento dell'hip hop nostrano.
          </p>
          <ul className="text-gray-300 space-y-3 list-disc list-inside mb-4">
            <li className="leading-relaxed">
              <strong className="text-white">Le Collaborazioni:</strong> chiama a raccolta il meglio della scena italiana, tra cui <strong className="text-white">Club Dogo, Inoki, Bassi Maestro, Stokka &amp; MadBuddy, Tormento</strong> e <strong className="text-white">Fabri Fibra</strong>.
            </li>
            <li className="leading-relaxed">
              <strong className="text-white">L'Eredità:</strong> tracce come <em>Ghettoblaster</em> e <em>Bolo by Night</em> diventano inni generazionali. 60 Hz non è solo un album, ma un manifesto di come si possa fare rap in Italia mantenendo un'attitudine cruda e autentica.
            </li>
          </ul>

          <h4 className="text-xl font-bold mt-6 mb-3 text-red-400" style={{ fontFamily: 'Oswald, sans-serif' }}>L'Impero "Unlimited Struggle"</h4>
          <p className="text-gray-300 mb-4 leading-relaxed">
            Insieme all'amico e collaboratore di sempre <strong className="text-white">Frank Siciliano</strong>, DJ Shocca dà vita a <strong className="text-red-400">Unlimited Struggle</strong>, inizialmente una crew e successivamente una vera e propria etichetta discografica indipendente.
          </p>
          <p className="text-gray-300 mb-4 leading-relaxed">
            Sotto l'egida di Unlimited Struggle sono cresciuti e si sono affermati artisti che hanno scritto la storia del genere, come <strong className="text-white">Mistaman, Stokka &amp; MadBuddy, Ghemon, Nex Cassel</strong> e <strong className="text-white">Louis Dee</strong>.
          </p>
          <p className="text-gray-300 mb-4 leading-relaxed">
            Shocca diventa il <strong className="text-white">direttore artistico</strong> e il produttore di riferimento della label, sfornando beat per classici come <strong className="text-white">Struggle Music</strong> (album corale del 2007) e innumerevoli dischi solisti dei membri del roster.
          </p>

          <h4 className="text-xl font-bold mt-6 mb-3 text-red-400" style={{ fontFamily: 'Oswald, sans-serif' }}>Sacrosanto e il Presente (ad oggi, 2026)</h4>
          <p className="text-gray-300 mb-4 leading-relaxed">
            Nonostante i cambiamenti delle mode e l'avvento di nuovi sottogeneri, DJ Shocca non ha mai snaturato il suo suono, rimanendo un <strong className="text-white">faro per i "puristi"</strong> e guadagnandosi il rispetto incondizionato anche dalle nuove generazioni.
          </p>
          <ul className="text-gray-300 space-y-3 list-disc list-inside mb-4">
            <li className="leading-relaxed">
              <strong className="text-white">Il Ritorno discografico:</strong> nel <strong className="text-white">2023</strong> ha pubblicato <strong className="text-red-400">"Sacrosanto"</strong>, un album che ha sancito il suo ritorno in grande stile, ricco di ospiti di peso (da <strong className="text-white">Guè</strong> ed <strong className="text-white">Emis Killa</strong>, fino a <strong className="text-white">Danno, Inoki</strong> e <strong className="text-white">Kaos</strong>), dimostrando che il suo tocco sui pad e sui campionatori è letteralmente senza tempo.
            </li>
            <li className="leading-relaxed">
              <strong className="text-white">Attività Live e Beatmaking:</strong> oggi Shocca continua a calcare i palchi di tutta Italia con DJ set infuocati che sono vere e proprie lezioni di storia della musica black. Continua inoltre a produrre, collaborare con la scena e a gestire il suo rinomato studio di registrazione, mantenendo vivo lo spirito originario della cultura Hip Hop.
            </li>
          </ul>
        </>
      ),
      links: []
    },
    {
      id: 'dj-kamo',
      name: "DJ KAMO",
      realName: "DJ Kamo",
      image: 'https://customer-assets.emergentagent.com/job_site-mirror-165/artifacts/43b5or0o_kamooriginal600.jpg',
      role: 'DJ, Selecta, Producer',
      shortBio: "Colonna portante della scena di Genova, DJ ufficiale di Tecniche Perfette, selecta e divulgatore.",
      videoId: 'OlYTbfQzmC8',
      videoLabel: 'GUARDA IL VIDEO',
      fullBio: (
        <>
          <p className="text-gray-300 mb-4 leading-relaxed">
            <strong className="text-white">DJ Kamo</strong>, colonna portante della scena di <strong className="text-white">Genova</strong> e <strong className="text-red-400">DJ ufficiale di Tecniche Perfette</strong>, è uno dei selecta, produttori e divulgatori più attivi e appassionati dell'hip hop in Italia.
          </p>
          <p className="text-gray-400 text-sm mb-6 italic">
            Di seguito la biografia completa aggiornata ad aprile 2026.
          </p>

          <h4 className="text-xl font-bold mt-2 mb-3 text-red-400" style={{ fontFamily: 'Oswald, sans-serif' }}>Biografia</h4>
          <p className="text-gray-300 mb-4 leading-relaxed">
            Classe <strong className="text-white">1980</strong>, nato e cresciuto a <strong className="text-white">Genova</strong>, DJ Kamo è molto più di un disc jockey: è un vero e proprio <strong className="text-white">"architetto" della scena urban ligure</strong> e nazionale. Con quasi trent'anni di carriera alle spalle, la sua storia personale si intreccia a doppio filo con l'evoluzione del rap a Genova, dai marciapiedi fino ai palchi di tutta Italia.
          </p>

          <h4 className="text-xl font-bold mt-6 mb-3 text-red-400" style={{ fontFamily: 'Oswald, sans-serif' }}>Le Origini: Zena e il Sogno Hip Hop</h4>
          <p className="text-gray-300 mb-4 leading-relaxed">
            Il percorso di Kamo inizia negli <strong className="text-white">anni '90</strong>, un'epoca in cui Genova doveva ancora ritagliarsi il suo spazio sulla mappa dell'hip hop italiano. Ispirato dal sound americano e cresciuto con i classici del rap e del soul, inizia a far girare i dischi sognando di rendere la sua città una <strong className="text-white">"capitale del rap"</strong>.
          </p>
          <p className="text-gray-300 mb-4 leading-relaxed">
            Diventa rapidamente un punto di riferimento per l'intera comunità locale: suona nei club, partecipa a jam spontanee e contribuisce alla creazione di realtà storiche e crew (come il circuito <strong className="text-white">Zena Art Core</strong>), supportando l'ascesa dei primi rapper genovesi e facendo da ponte tra breaker, freestyler e MC.
          </p>

          <h4 className="text-xl font-bold mt-6 mb-3 text-red-400" style={{ fontFamily: 'Oswald, sans-serif' }}>Dietro la Consolle: Tecniche Perfette e il Clubbing</h4>
          <p className="text-gray-300 mb-4 leading-relaxed">
            L'attitudine cruda e l'amore viscerale per il <strong className="text-white">turntablism</strong> lo portano a incrociare i piatti con le realtà più solide del paese.
          </p>
          <ul className="text-gray-300 space-y-3 list-disc list-inside mb-4">
            <li className="leading-relaxed">
              <strong className="text-white">Tecniche Perfette:</strong> forte della sua versatilità e della sua capacità di leggere il pubblico e gli MC, Kamo ha consolidato il suo ruolo come <strong className="text-red-400">DJ ufficiale per Tecniche Perfette</strong>, la madre di tutte le battle di freestyle italiane. I suoi beat e i suoi cut (presenti anche nelle leggendarie raccolte strumentali <strong className="text-white">Beats for Freestyle</strong>) hanno fatto da tappeto alle sfide più incendiarie del format.
            </li>
            <li className="leading-relaxed">
              <strong className="text-white">Il Live e il Club:</strong> ha suonato nei migliori club del Nord Italia, portando un sound ricercato che mescola <strong className="text-white">rap classico, R&amp;B e influenze club anni 2000</strong>, e animando serate iconiche con format come <strong className="text-white">Best Dj's In Town</strong>.
            </li>
          </ul>

          <h4 className="text-xl font-bold mt-6 mb-3 text-red-400" style={{ fontFamily: 'Oswald, sans-serif' }}>Produzione, Dischi e Radio</h4>
          <p className="text-gray-300 mb-4 leading-relaxed">
            Oltre alla dimensione live, Kamo è un instancabile creatore di progetti in studio e format di divulgazione:
          </p>
          <ul className="text-gray-300 space-y-3 list-disc list-inside mb-4">
            <li className="leading-relaxed">
              <strong className="text-white">Le Produzioni:</strong> fin dai primi anni 2000 ha prodotto demo e mixtape per artisti locali (contribuendo a far sbocciare la nuova generazione di rapper genovesi), per poi pubblicare progetti personali di spessore, come il disco ufficiale <strong className="text-red-400">"Changes"</strong> (2013), un concentrato di tecnica e collaborazioni di livello.
            </li>
            <li className="leading-relaxed">
              <strong className="text-white">La Voce in Radio:</strong> oltre a far parlare i dischi, ha prestato la sua competenza alla radio. Negli anni ha curato format, mixtape e podcast (come l'esperienza fondamentale con <strong className="text-white">RapBeats Radio</strong>), diventando un divulgatore attento e meticoloso della cultura a 360 gradi.
            </li>
          </ul>

          <h4 className="text-xl font-bold mt-6 mb-3 text-red-400" style={{ fontFamily: 'Oswald, sans-serif' }}>Oggi (2026)</h4>
          <p className="text-gray-300 leading-relaxed">
            Oggi DJ Kamo rappresenta l'<strong className="text-white">anima più autentica del rap indipendente</strong>. È venerato dalla nuova generazione ligure, non solo come pioniere, ma come maestro che ha saputo gettare le basi affinché Genova esplodesse a livello nazionale. Continua ininterrottamente a far suonare i suoi dischi, a sfornare produzioni e a tenere alto l'onore della doppia H, dimostrando che la <strong className="text-red-400">vera attitudine hip hop non scade mai</strong>.
          </p>
        </>
      ),
      links: []
    },
    {
      id: 'dj-ms',
      name: "DJ MS",
      realName: "Simone Meneguzzo",
      image: 'https://customer-assets.emergentagent.com/job_site-mirror-165/artifacts/4z46jeka_IMG_20251201_215537.jpg',
      role: 'DJ, Producer, Talent Scout',
      shortBio: "DJ ufficiale di Nitro, fondatore Gold Leaves Academy e co-creatore di Mic Tyson. Piatti in varie tappe/finali TP.",
      videoId: '-NpexoOr-7o',
      videoLabel: 'GUARDA IL VIDEO',
      fullBio: (
        <>
          <p className="text-gray-300 mb-4 leading-relaxed">
            <strong className="text-white">DJ MS</strong>, all'anagrafe <strong className="text-white">Simone Meneguzzo</strong>, è un disc jockey, produttore e talent scout di <strong className="text-white">Vicenza</strong>. Attivo dai primi anni 2000, è noto per essere il <strong className="text-red-400">DJ ufficiale di Nitro</strong>, fondatore della label <strong className="text-white">Gold Leaves Academy</strong>, co-creatore del format <strong className="text-white">Mic Tyson</strong> e per aver suonato ai piatti in alcune tappe e finali dello storico contest <strong className="text-red-400">Tecniche Perfette</strong>.
          </p>
          <p className="text-gray-300 mb-6 leading-relaxed">
            Nato a Vicenza nel <strong className="text-white">1987</strong>, Simone Meneguzzo, in arte DJ MS, rappresenta una delle figure più dinamiche, professionali e influenti del djing rap in Italia. Sulla scena dal <strong className="text-white">2003</strong>, la sua carriera è l'esempio perfetto di come la dedizione alla vera cultura hip hop possa portare un artista dai centri sociali della provincia veneta ai più grandi palchi nazionali.
          </p>

          <h4 className="text-xl font-bold mt-6 mb-3 text-red-400" style={{ fontFamily: 'Oswald, sans-serif' }}>Gli Inizi, le Battle e il Legame con Tecniche Perfette</h4>
          <p className="text-gray-300 mb-4 leading-relaxed">
            La formazione di DJ MS avviene orgogliosamente <strong className="text-white">"sul campo"</strong>. Cresciuto col mito del DJ come figura chiave e regista dietro le quinte, si appassiona visceralmente all'arte del <strong className="text-white">turntablism</strong> e del freestyle. Le sue primissime esperienze dietro ai piatti in contesti di battle si svolgono al <strong className="text-white">Centro Sociale Bocciodromo di Vicenza</strong> (durante eventi storici come il <strong className="text-white">King of the Ring</strong>), e da lì il passo verso le competizioni su scala nazionale è breve.
          </p>
          <p className="text-gray-300 mb-4 leading-relaxed">
            La sua attitudine, la tecnica pulita e il tocco infallibile lo portano a collaborare strettamente con il circuito di <strong className="text-red-400">Tecniche Perfette</strong>, diventando il selecta ufficiale in svariate tappe regionali e finali del format. Una gavetta fondamentale che lo ha consacrato come uno dei migliori <strong className="text-white">"DJ da battaglia"</strong> del Paese, capace di leggere chirurgicamente i tempi dell'MC e servire il beat perfetto al momento giusto.
          </p>

          <h4 className="text-xl font-bold mt-6 mb-3 text-red-400" style={{ fontFamily: 'Oswald, sans-serif' }}>Il Sodalizio con Nitro e il fenomeno Mic Tyson</h4>
          <p className="text-gray-300 mb-4 leading-relaxed">
            Il suo talento non passa inosservato, ed è la sinergia con un altro peso massimo vicentino a segnare uno snodo cruciale per la sua carriera: <strong className="text-white">Nitro</strong>. DJ MS diventa la sua inseparabile spalla live, accompagnandolo in ogni tour, dai club underground fino ai palchi giganteschi, entrando a pieno titolo nell'orbita e nelle dinamiche live della <strong className="text-white">Machete Crew</strong>.
          </p>
          <p className="text-gray-300 mb-4 leading-relaxed">
            L'amore per le sfide di rime non si è però mai spento: unendo le forze e la visione manageriale proprio con Nitro, MS ha dato vita e organizzato il <strong className="text-red-400">Mic Tyson</strong>, format che negli anni è letteralmente esploso, diventando uno degli eventi di culto e di maggior successo per il freestyle in Italia, con edizioni andate regolarmente sold-out in templi della musica come i <strong className="text-white">Magazzini Generali di Milano</strong>.
          </p>

          <h4 className="text-xl font-bold mt-6 mb-3 text-red-400" style={{ fontFamily: 'Oswald, sans-serif' }}>Gold Leaves Academy: Visione e Scouting</h4>
          <p className="text-gray-300 mb-4 leading-relaxed">
            Non solo piatti e mixer. Con la mentalità di chi vuole far prosperare l'intero ecosistema urban, nel <strong className="text-white">2017</strong> Simone ha fondato la <strong className="text-red-400">Gold Leaves Academy</strong>, un'etichetta indipendente e agenzia di management nata a Vicenza e successivamente allargatasi a Milano. Con questa realtà, MS si impegna in prima linea a <strong className="text-white">scovare talenti emergenti</strong>, curandone la direzione artistica, smussando il suono e accompagnandoli passo dopo passo all'interno dell'industria discografica.
          </p>

          <h4 className="text-xl font-bold mt-6 mb-3 text-red-400" style={{ fontFamily: 'Oswald, sans-serif' }}>Produzione e Attitudine</h4>
          <p className="text-gray-300 mb-4 leading-relaxed">
            Musicalmente, DJ MS ha sempre dichiarato di non credere in una netta frattura tra <strong className="text-white">"old school"</strong> e <strong className="text-white">"new school"</strong>. Sebbene le sue radici peschino a piene mani dal funk, dal soul e dall'estetica <strong className="text-white">boom bap</strong> dei primi anni 2000, il suo approccio è in continua evoluzione e contaminazione. Passa intere giornate in studio a raffinare beat, limare cut e studiare sonorità, mettendosi a disposizione dei rapper con l'umiltà e la dedizione dei veri maestri di cerimonia sonori.
          </p>

          <h4 className="text-xl font-bold mt-6 mb-3 text-red-400" style={{ fontFamily: 'Oswald, sans-serif' }}>Oggi (2026)</h4>
          <p className="text-gray-300 leading-relaxed">
            Oggi DJ MS è considerato all'unanimità una <strong className="text-white">colonna portante della scena</strong>. Tra dj-set incendiari, l'organizzazione di eventi che dettano il mercato del freestyle, il lavoro discografico e la continua vita on the road insieme a Nitro, Simone Meneguzzo incarna la vera essenza del djing moderno: <strong className="text-red-400">profondo rispetto per le radici, fiuto per il talento e un lavoro instancabile per la cultura Hip Hop</strong>.
          </p>
        </>
      ),
      links: []
    }
  ];

  // Content Creator ufficiali
  const creators = [
    {
      id: 'arcade-boyz',
      name: "ARCADE BOYZ",
      realName: "Fada & Barlow",
      image: 'https://customer-assets.emergentagent.com/job_site-mirror-165/artifacts/gp037w39_arcade%20boyz.jpg',
      role: 'YouTuber, Content Creator',
      shortBio: "Duo di YouTuber celebri per reaction e critica musicale. Hanno commentato le battle TP e sostenuto MC emergenti.",
      videoId: 'OTAsWetEAYg',
      videoLabel: 'GUARDA IL VIDEO',
      fullBio: (
        <>
          <p className="text-gray-300 mb-4 leading-relaxed">
            Gli <strong className="text-white">Arcade Boyz</strong> sono uno dei duo di content creator e YouTuber più celebri, influenti e discussi d'Italia. Diventati famosi grazie ai loro video di <strong className="text-white">"reaction" e critica musicale</strong>, hanno portato sulla piattaforma un linguaggio crudo, diretto e totalmente privo di filtri.
          </p>
          <p className="text-gray-300 mb-4 leading-relaxed">
            Hanno commentato da casa in diretta — e talvolta dal vivo — le battle dei freestyler e curato diversi speciali dedicati agli MC usciti da <strong className="text-red-400">Tecniche Perfette</strong>.
          </p>
          <p className="text-gray-300 mb-6 leading-relaxed">
            Dietro lo schermo (e seduti sul loro iconico divano) ci sono due figure originarie della provincia di <strong className="text-white">Novara</strong>, complementari per stile e approccio: <strong className="text-white">Daniele Fadda</strong> (in arte <strong className="text-purple-400">Fada</strong>) ed <strong className="text-white">Eduardo Turco</strong> (in arte <strong className="text-purple-400">Barlow</strong>).
          </p>

          <h4 className="text-xl font-bold mt-6 mb-3 text-purple-400" style={{ fontFamily: 'Oswald, sans-serif' }}>La Storia e l'Impatto del Duo</h4>
          <p className="text-gray-300 mb-4 leading-relaxed">
            Nati su YouTube a <strong className="text-white">metà degli anni 2010</strong>, gli Arcade Boyz hanno rivoluzionato il concetto di recensione musicale sul web italiano. Non si limitavano ad ascoltare i brani, ma li dissezionavano: valutavano <strong className="text-white">metriche, produzioni, messaggi e attitudine</strong> degli artisti.
          </p>
          <ul className="text-gray-300 space-y-3 list-disc list-inside mb-4">
            <li className="leading-relaxed">
              <strong className="text-white">L'Approccio "da Bar":</strong> hanno sempre rivendicato con orgoglio di essere YouTuber e creatori di contenuti, prendendo le distanze dalla figura patinata dell'influencer. Il loro set tipico — una sala giochi o un semplice divano — rifletteva una chiacchierata informale tra amici, ma capace di generare dibattiti enormi.
            </li>
            <li className="leading-relaxed">
              <strong className="text-white">Tra Controversie e Scouting:</strong> il loro stile tagliente e provocatorio li ha spesso portati a scontrarsi apertamente con artisti mainstream e fanbase agguerrite, sfociando in alcuni casi anche in note controversie legali e mediatiche (come la nota querelle con <strong className="text-white">Selvaggia Lucarelli</strong>). Tuttavia, hanno dimostrato un fiuto eccezionale per i nuovi talenti, supportando artisti emergenti fin dai loro esordi. Un esempio emblematico è <strong className="text-white">Madame</strong>, che ricevette uno dei primissimi supporti mediatici proprio tramite il loro format.
            </li>
            <li className="leading-relaxed">
              <strong className="text-white">L'Evoluzione:</strong> recentemente le dinamiche del duo hanno subito delle pause e delle fisiologiche evoluzioni (con Fada che ha intrapreso anche percorsi solisti e allontanamenti dal progetto principale), ma il marchio Arcade Boyz rimane una pietra miliare per l'intrattenimento musicale in Italia.
            </li>
          </ul>

          <h4 className="text-xl font-bold mt-6 mb-3 text-purple-400" style={{ fontFamily: 'Oswald, sans-serif' }}>Le Singole Biografie</h4>

          <h5 className="text-lg font-bold mt-4 mb-2 text-white" style={{ fontFamily: 'Oswald, sans-serif' }}>Daniele "Fada" Fadda</h5>
          <p className="text-gray-300 mb-3 leading-relaxed">
            Classe <strong className="text-white">1982</strong>, Daniele Fadda è sempre stata <strong className="text-white">"l'anima tecnica"</strong> e analitica degli Arcade Boyz.
          </p>
          <ul className="text-gray-300 space-y-3 list-disc list-inside mb-4">
            <li className="leading-relaxed">
              <strong className="text-white">Il Profilo:</strong> con un background fortemente radicato nella cultura hip hop e nel rap cantato, Fada ha sempre portato nei video un orecchio clinico. Era la metà del duo capace di smontare un testo per analizzare gli incastri, le figure retoriche e la complessità metrica.
            </li>
            <li className="leading-relaxed">
              <strong className="text-white">Il Ruolo:</strong> più focalizzato sugli aspetti tecnici, ha rappresentato per anni la voce del <strong className="text-white">"purista"</strong>, criticando aspramente le derive più plastificate o povere di contenuti della nuova scena trap e difendendo i valori originali della scrittura rap.
            </li>
          </ul>

          <h5 className="text-lg font-bold mt-4 mb-2 text-white" style={{ fontFamily: 'Oswald, sans-serif' }}>Eduardo "Barlow" Turco</h5>
          <p className="text-gray-300 mb-3 leading-relaxed">
            Classe <strong className="text-white">1988</strong>, Eduardo Turco è <strong className="text-white">l'energia pura, vulcanica e istintiva</strong> del progetto.
          </p>
          <ul className="text-gray-300 space-y-3 list-disc list-inside mb-4">
            <li className="leading-relaxed">
              <strong className="text-white">Il Profilo:</strong> se Fada metteva la tecnica, Barlow portava <strong className="text-white">l'intrattenimento, la provocazione e la reazione di pancia</strong>. Con un linguaggio colorito, sfacciato e ricco di tormentoni, ha saputo creare un legame empatico potentissimo con i fan.
            </li>
            <li className="leading-relaxed">
              <strong className="text-white">Il Ruolo:</strong> Barlow ha sempre mantenuto il focus sull'impatto sociale della musica e sulle mode dei teenager, parlando la lingua della strada senza filtri né moralismi. La sua capacità di generare dinamiche comiche e taglienti è stata il motore trainante per la viralità di centinaia di video del canale.
            </li>
          </ul>
        </>
      )
    },
    {
      id: 'rend',
      name: "REND",
      realName: "Emanuele Beltrami",
      image: 'https://customer-assets.emergentagent.com/job_site-mirror-165/artifacts/w2vjzi7a_WhatsApp%20Image%202026-04-21%20at%2022.15.32.jpeg',
      role: 'Content Creator, Host, Ideatore Format',
      shortBio: "Volto di Freestyle Reaction, co-creatore di FreestyleMania e The Dome. Giurato e host di eventi TP.",
      videoId: 'wI8Xg2a6Uag',
      videoLabel: 'GUARDA IL VIDEO',
      teaserVideoId: 'wI8Xg2a6Uag',
      fullBio: (
        <>
          <p className="text-gray-300 mb-4 leading-relaxed">
            <strong className="text-white">Rend</strong>, all'anagrafe <strong className="text-white">Emanuele Beltrami</strong>, è uno dei volti più noti, attivi e influenti dell'ecosistema del freestyle italiano moderno. <strong className="text-purple-400">Content creator, ideatore di format, host e giurato</strong>, rappresenta una figura cardine per l'evoluzione e la divulgazione delle rap battle, sia sul web che dal vivo.
          </p>
          <p className="text-gray-300 mb-6 leading-relaxed">
            Nato con una passione viscerale per la cultura hip hop e per l'arte dell'improvvisazione, Emanuele "Rend" Beltrami ha saputo ritagliarsi un ruolo unico nella scena italiana. Più che limitarsi a competere col microfono in mano, ha intuito prima di molti altri la necessità di <strong className="text-white">raccontare, analizzare e innovare</strong> il mondo del freestyle, creando un ponte solido tra gli MC, i palchi storici e il grande pubblico di internet.
          </p>

          <h4 className="text-xl font-bold mt-6 mb-3 text-purple-400" style={{ fontFamily: 'Oswald, sans-serif' }}>L'Innovazione sul Web: Freestyle Reaction e The Dome</h4>
          <p className="text-gray-300 mb-4 leading-relaxed">
            Il nome di Rend si consolida grazie al suo lavoro instancabile sulle piattaforme digitali, in particolare <strong className="text-white">YouTube e Twitch</strong>, dove ha letteralmente cambiato il modo in cui le battle vengono percepite e consumate dai fan:
          </p>
          <ul className="text-gray-300 space-y-3 list-disc list-inside mb-4">
            <li className="leading-relaxed">
              <strong className="text-white">Freestyle Reaction:</strong> è diventato un punto di riferimento assoluto grazie al suo format di reazione e analisi delle sfide di freestyle. Con un occhio critico, competenza tecnica e un linguaggio accessibile, ha educato migliaia di nuovi spettatori alle dinamiche delle battle, spiegando l'importanza delle punchline, delle metriche, dei wordplay e dell'attitudine.
            </li>
            <li className="leading-relaxed">
              <strong className="text-white">The Dome:</strong> la sua visione lo ha portato a essere tra i motori creativi di <em>The Dome</em>, una vera e propria <strong className="text-white">"TV del freestyle"</strong> nata su Twitch. Un progetto ambizioso progettato per espandere il concetto di improvvisazione, offrendo talk show, interviste, approfondimenti e generando una community affiatata attorno alla disciplina.
            </li>
          </ul>

          <h4 className="text-xl font-bold mt-6 mb-3 text-purple-400" style={{ fontFamily: 'Oswald, sans-serif' }}>I Format dal Vivo: FreestyleMania</h4>
          <p className="text-gray-300 mb-4 leading-relaxed">
            L'attitudine di Rend non si ferma però dietro la videocamera. Mettendo in campo la sua influenza e la sua profonda conoscenza del roster di freestyler italiani, è diventato un creatore di eventi live di grande successo:
          </p>
          <ul className="text-gray-300 space-y-3 list-disc list-inside mb-4">
            <li className="leading-relaxed">
              <strong className="text-red-400">FreestyleMania:</strong> nato nel <strong className="text-white">2018</strong> da un'idea sviluppata a stretto contatto con <strong className="text-white">Mastafive</strong> (e che ha visto il coinvolgimento degli stessi Arcade Boyz per la risonanza web), <em>FreestyleMania</em> ha portato in Italia una dinamica inedita: la <strong className="text-white">battle a squadre 3 contro 3</strong>. Chiamando a raccolta i campioni più titolati d'Italia (divisi in team capitanati da nomi come Morbo, Drimer, Blnkay e Shekkero), il format ha unito la competizione spietata allo spettacolo di puro intrattenimento, vedendo Rend in prima linea come mente organizzativa e co-conduttore.
            </li>
          </ul>

          <h4 className="text-xl font-bold mt-6 mb-3 text-purple-400" style={{ fontFamily: 'Oswald, sans-serif' }}>Il Legame con Tecniche Perfette</h4>
          <p className="text-gray-300 mb-4 leading-relaxed">
            Il rispetto guadagnato sia dalla fanbase che dagli addetti ai lavori lo ha portato a stringere una forte e continua sinergia proprio con <strong className="text-red-400">Tecniche Perfette</strong>.
          </p>
          <p className="text-gray-300 mb-4 leading-relaxed">
            Nel corso degli anni, Rend è stato chiamato in diverse occasioni a collaborare con Mastafive, ricoprendo ruoli di <strong className="text-white">giurato o di supporto alla conduzione</strong> in eventi legati al brand (come ad esempio le edizioni speciali in collaborazione con <strong className="text-white">Pagella Non Solo Rock</strong> a Torino). La sua presenza certifica un ponte essenziale tra la storicità della competizione "madre" di tutte le battle e il linguaggio delle nuove generazioni.
          </p>

          <h4 className="text-xl font-bold mt-6 mb-3 text-purple-400" style={{ fontFamily: 'Oswald, sans-serif' }}>Oggi (2026)</h4>
          <p className="text-gray-300 mb-6 leading-relaxed">
            Ad oggi, Emanuele Beltrami è indiscutibilmente <strong className="text-white">uno dei massimi divulgatori e promotori del freestyle in Italia</strong>. Che stia strutturando un nuovo torneo, conducendo una diretta su Twitch o valutando le rime dei futuri campioni al tavolo della giuria, Rend continua a dedicare la sua carriera alla crescita della disciplina. Ha dimostrato ai fatti che, per mantenere viva e prospera la cultura Hip Hop, c'è un bisogno vitale non solo di chi sta sul palco a sputare rime, ma anche di <strong className="text-red-400">chi sa costruire il palco stesso e raccontarne la magia</strong> a chi sta sotto.
          </p>

          {/* Embed secondario: Freestyle Reaction playlist */}
          <div className="mt-6 rounded-lg overflow-hidden border border-gray-700" style={{ backgroundColor: 'rgba(0,0,0,0.4)' }}>
            <div className="px-4 py-3 border-b border-gray-700">
              <h5 className="text-base font-bold text-purple-400" style={{ fontFamily: 'Oswald, sans-serif' }}>
                FREESTYLE REACTION — PLAYLIST
              </h5>
            </div>
            <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
              <iframe
                src="https://www.youtube.com/embed/QAGBIPqLyV0?list=PLUTXL6aCnxEAMOstHpNJmH9aS-xBwg4Gu"
                title="Rend - Freestyle Reaction Playlist"
                data-testid="rend-playlist"
                className="absolute top-0 left-0 w-full h-full"
                style={{ border: 0 }}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
              />
            </div>
          </div>
        </>
      )
    }
  ];




  const sections = [
    {
      id: 'cosa-tp',
      title: 'COSA È "TECNICHE PERFETTE" E CHI SONO I GATE KEEPAZ',
      image: "https://static.wixstatic.com/media/e4aa06_9d66f8c9a6fe4ea4a8c4dcd1f34e30a8~mv2.jpg/v1/fill/w_400,h_300,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/24254245_521739434853347_568483565233661.jpg",
      shortDesc: "La storia di Tecniche Perfette e la cultura Hip Hop italiana",
      fullContent: (
        <>
          <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Oswald, sans-serif' }}>Gate Keepaz : Originatori di Tecniche Perfette</h3>
          <p className="text-gray-300 mb-4 leading-relaxed">
            Tecniche Perfette è una manifestazione legata all'Hip Hop, prende nome dall'omonimo pezzo dei GateKeepaz (<a href="http://www.gatekeyz.com" className="text-red-400 hover:underline" target="_blank" rel="noopener noreferrer">www.gatekeyz.com</a>), nel quale vengono esaltate le potenzialità vere di un MC.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            <Dialog>
              <DialogTrigger asChild>
                <img src="https://static.wixstatic.com/media/e4aa06_fda40c4261764e7bbd20618d06954e16~mv2.jpg/v1/fill/w_192,h_145,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/hqdefault.jpg" alt="Gate Keepaz crew 1996" className="rounded cursor-pointer hover:scale-105 transition-transform duration-300" />
              </DialogTrigger>
              <DialogContent className="bg-gray-900 border-gray-700 max-w-[95vw] sm:max-w-4xl p-2">
                <DialogTitle className="sr-only">Gate Keepaz crew 1996</DialogTitle>
                <img src="https://static.wixstatic.com/media/e4aa06_fda40c4261764e7bbd20618d06954e16~mv2.jpg/v1/fill/w_1920,h_1080,al_c,q_90,enc_avif,quality_auto/hqdefault.jpg" alt="Gate Keepaz crew 1996" className="w-full rounded-lg" />
                <p className="text-white text-center mt-2 pb-2 text-sm">Gate Keepaz crew 1996</p>
              </DialogContent>
            </Dialog>
            <Dialog>
              <DialogTrigger asChild>
                <img src="https://static.wixstatic.com/media/e4aa06_9d66f8c9a6fe4ea4a8c4dcd1f34e30a8~mv2.jpg/v1/fill/w_209,h_145,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/24254245_521739434853347_568483565233661.jpg" alt="Gate Keepaz 2017" className="rounded cursor-pointer hover:scale-105 transition-transform duration-300" />
              </DialogTrigger>
              <DialogContent className="bg-gray-900 border-gray-700 max-w-[95vw] sm:max-w-4xl p-2">
                <DialogTitle className="sr-only">Gate Keepaz 2017</DialogTitle>
                <img src="https://static.wixstatic.com/media/e4aa06_9d66f8c9a6fe4ea4a8c4dcd1f34e30a8~mv2.jpg/v1/fill/w_1920,h_1080,al_c,q_90,enc_avif,quality_auto/24254245_521739434853347_568483565233661.jpg" alt="Gate Keepaz 2017" className="w-full rounded-lg" />
                <p className="text-white text-center mt-2 pb-2 text-sm">Gate Keepaz 2017</p>
              </DialogContent>
            </Dialog>
            <Dialog>
              <DialogTrigger asChild>
                <img src="https://static.wixstatic.com/media/e4aa06_014ea7e688054c268f7358d0cfbcff8c~mv2.jpg/v1/fill/w_316,h_198,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/108724276_8d32ca23d7_b.jpg" alt="Gate Keepaz 1999" className="rounded cursor-pointer hover:scale-105 transition-transform duration-300" />
              </DialogTrigger>
              <DialogContent className="bg-gray-900 border-gray-700 max-w-[95vw] sm:max-w-4xl p-2">
                <DialogTitle className="sr-only">Gate Keepaz 1999</DialogTitle>
                <img src="https://static.wixstatic.com/media/e4aa06_014ea7e688054c268f7358d0cfbcff8c~mv2.jpg/v1/fill/w_1920,h_1080,al_c,q_90,enc_avif,quality_auto/108724276_8d32ca23d7_b.jpg" alt="Gate Keepaz 1999" className="w-full rounded-lg" />
                <p className="text-white text-center mt-2 pb-2 text-sm">Gate Keepaz 1999</p>
              </DialogContent>
            </Dialog>
            <Dialog>
              <DialogTrigger asChild>
                <img src="https://static.wixstatic.com/media/e93d0f_48e1072ffb7a401481bedcbbd1c1b313~mv2.jpeg/v1/fill/w_217,h_275,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/WhatsApp%20Image%202024-03-04%20at%2000_00_35.jpeg" alt="Tecniche Perfette" className="rounded cursor-pointer hover:scale-105 transition-transform duration-300" />
              </DialogTrigger>
              <DialogContent className="bg-gray-900 border-gray-700 max-w-[95vw] sm:max-w-4xl p-2">
                <DialogTitle className="sr-only">Tecniche Perfette</DialogTitle>
                <img src="https://static.wixstatic.com/media/e93d0f_48e1072ffb7a401481bedcbbd1c1b313~mv2.jpeg/v1/fill/w_1920,h_1080,al_c,q_90,enc_avif,quality_auto/WhatsApp%20Image%202024-03-04%20at%2000_00_35.jpeg" alt="Tecniche Perfette" className="w-full rounded-lg" />
                <p className="text-white text-center mt-2 pb-2 text-sm">Tecniche Perfette</p>
              </DialogContent>
            </Dialog>
          </div>
          <p className="text-gray-300 mb-4 leading-relaxed">
            Nasce quindi il contest di Freestyle (improvvisazione in rima su basi strumentali) più seguito d'Italia: dal 2003 grazie all'aiuto e allo sforzo dello staff del TP presente in ogni regione, ogni anno si cerca in giro per la penisola il migliore MC da battaglia.
          </p>
          <p className="text-gray-300 mb-4 leading-relaxed">
            <strong>Sono usciti dal T.P.</strong> mc's del calibro di Mondo Marcio, Ensi, Clementino, Emis Killa, Fedez, Cane Secco, Kenzie, Moreno, Mad Man, Johnny Roy, Dank, Nitro
          </p>
          <p className="text-gray-300 mb-4 leading-relaxed">
            <strong>I vincitori delle edizioni sono:</strong> Mondo Marcio, ENSI, Clementino, July B, Emis Killa, Noema, CaneSecco, Kenzie, Moreno MC, Kenzie, Fatt, Lethal V, MAIK BRAIN, Morbo, Shekkero, BLNK
          </p>
          <p className="text-gray-300 mb-4 leading-relaxed">
            Sui nostri palchi si sono esibiti tutti i migliori (Kiave, Mista, Jesto e Hyst, Rayden, Raige, Deal, Stokka e Mad Buddy, Kaos, Colle Der Fomento, Maury B, Lefty...)
          </p>
          <p className="text-gray-300 mb-4 leading-relaxed">
            L'intento dichiarato della manifestazione, ideata da <a href="http://www.djdoubles.com/" className="text-red-400 hover:underline" target="_blank" rel="noopener noreferrer">DJ Double S</a> e <a href="http://www.mastafive.com/" className="text-red-400 hover:underline" target="_blank" rel="noopener noreferrer">Mastafive</a>, esponenti di spicco della scena torinese, è promuovere la Kultura Hip Hop evitando strumentalizzazioni mediatiche, recuperando vecchie regole di questo genere, prima tra tutte quella secondo la quale il rispetto va guadagnato sul campo, solcando i palchi microfono in pugno.
          </p>
          <h4 className="text-xl font-bold mt-6 mb-3" style={{ fontFamily: 'Oswald, sans-serif' }}>Le battle</h4>
          <p className="text-gray-300 leading-relaxed">
            Le battle (battaglia) sono le gare di Freestyle che per la competizione Tecniche Perfette si svolgono con la seguente modalità: "Roulette Russa", 30 secondi di presentazione dei Master of Ceremonies (MC) e scrematura a 16 partecipanti; "Stile Libero" ottavi, 1 minuto per ogni MC in una sfida 1 contro 1, "Top of the head" quarti, 1 minuto a testa e 1 minuto diviso in 4\4 alternati tra gli sfidanti, ogni volta che si necessita si invoca il "Second Round KO" dove gli mc si sfidano in un ulteriore minuto con argomento, e il "Giro della morte" in cui i due MCs finalisti si sfidano sui quattro quarti finché uno dei due non cede o vengono fermati.
          </p>

          {/* Video embed Saywaad Freestyle */}
          <div className="mt-8 rounded-lg overflow-hidden border border-gray-700" style={{ backgroundColor: 'rgba(0,0,0,0.4)' }}>
            <div className="px-4 py-3 border-b border-gray-700">
              <h5 className="text-base font-bold text-red-400" style={{ fontFamily: 'Oswald, sans-serif' }}>
                GUARDA IL VIDEO
              </h5>
            </div>
            <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
              <iframe
                src="https://www.youtube.com/embed/S-fpcxhvONc?start=331"
                title="Saywaad Freestyle - Tecniche Perfette"
                data-testid="cosa-tp-video"
                className="absolute top-0 left-0 w-full h-full"
                style={{ border: 0 }}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
              />
            </div>
          </div>
        </>
      )
    },
    {
      id: 'loghi-tp',
      title: 'STORIA DEI LOGHI DEL TECNICHE',
      image: "https://customer-assets.emergentagent.com/job_site-mirror-165/artifacts/y7fn14xz_logo%20tecncihe%20perfetteprimo.png",
      imageFit: 'contain',
      shortDesc: "Il primo logo storico del T.P. e la sua evoluzione nel tempo",
      fullContent: (
        <>
          <h3 className="text-2xl font-bold mb-2 text-red-400" style={{ fontFamily: 'Oswald, sans-serif' }}>
            PRIMO LOGO DEL T.P.
          </h3>
          <div className="w-16 h-1 mb-5" style={{ backgroundColor: 'var(--tp-red)' }} />

          {/* Affiancamento logo + ritratto */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <Dialog>
              <DialogTrigger asChild>
                <img
                  src="https://customer-assets.emergentagent.com/job_site-mirror-165/artifacts/y7fn14xz_logo%20tecncihe%20perfetteprimo.png"
                  alt="Primo logo Tecniche Perfette"
                  className="w-full aspect-square object-contain bg-red-600 rounded-lg cursor-pointer hover:scale-105 transition-transform duration-300 border border-gray-700"
                />
              </DialogTrigger>
              <DialogContent className="bg-gray-900 border-gray-700 max-w-[95vw] sm:max-w-3xl p-2">
                <DialogTitle className="sr-only">Primo logo Tecniche Perfette</DialogTitle>
                <img
                  src="https://customer-assets.emergentagent.com/job_site-mirror-165/artifacts/y7fn14xz_logo%20tecncihe%20perfetteprimo.png"
                  alt="Primo logo Tecniche Perfette"
                  className="w-full rounded-lg"
                />
                <p className="text-white text-center mt-2 pb-2 text-sm">Primo logo Tecniche Perfette — design originale Maphia Clothing</p>
              </DialogContent>
            </Dialog>

            <Dialog>
              <DialogTrigger asChild>
                <img
                  src="https://customer-assets.emergentagent.com/job_site-mirror-165/artifacts/y6ca0xim_669772048_18584835919024896_3697140882129385681_n.jpg"
                  alt="Bota — Stefano Rondolino"
                  className="w-full aspect-square object-cover rounded-lg cursor-pointer hover:scale-105 transition-transform duration-300 border border-gray-700"
                />
              </DialogTrigger>
              <DialogContent className="bg-gray-900 border-gray-700 max-w-[95vw] sm:max-w-3xl p-2">
                <DialogTitle className="sr-only">Bota — Stefano Rondolino</DialogTitle>
                <img
                  src="https://customer-assets.emergentagent.com/job_site-mirror-165/artifacts/y6ca0xim_669772048_18584835919024896_3697140882129385681_n.jpg"
                  alt="Bota — Stefano Rondolino"
                  className="w-full rounded-lg"
                />
                <p className="text-white text-center mt-2 pb-2 text-sm">Bota — Stefano Rondolino (Maphia Clothing)</p>
              </DialogContent>
            </Dialog>
          </div>

          <p className="text-gray-300 leading-relaxed mb-3">
            <strong className="text-white">Bota Stefano Rondolino</strong> è come si definisce lui stesso:
          </p>
          <ul className="text-gray-300 leading-relaxed mb-4 space-y-1 pl-2">
            <li>🤓 <span className="text-white">Nerd atipico</span></li>
            <li>✏️ <span className="text-white">Grafico e creativo</span></li>
            <li>☠️ <span className="text-white">Harleysta assetato</span></li>
          </ul>
          <p className="text-gray-300 leading-relaxed mb-6">
            È stato il primo sponsor a credere nel progetto di battle nazionale con la sua marca di abbigliamento{' '}
            <strong className="text-red-400">MAPHIA CLOTHING</strong>, il quale oltre a fornire il primo merch, ha ideato il
            primo logo di Tecniche Perfette.
          </p>

          {/* Pagina pubblicitaria DABomb 2003 — icona piccola */}
          <div className="border-t border-gray-800 pt-6 mt-6 mb-8">
            <p className="text-center text-xs uppercase tracking-widest text-gray-500 mb-3" style={{ fontFamily: 'Oswald, sans-serif' }}>
              Clicca per ingrandire
            </p>
            <Dialog>
              <DialogTrigger asChild>
                <div className="flex flex-col items-center cursor-pointer group w-fit mx-auto">
                  <img
                    src="https://customer-assets.emergentagent.com/job_site-mirror-165/artifacts/1ee2k8qj_primo%20flyer%20del%20tecniche.jpeg"
                    alt='Pagina pubblicitaria su "DABomb magazine" 2003'
                    className="w-32 h-32 sm:w-40 sm:h-40 object-cover rounded-lg border border-gray-700 group-hover:border-red-500 group-hover:scale-105 transition-all duration-300"
                  />
                  <p className="text-center text-gray-400 text-xs italic mt-2 max-w-[10rem]">
                    Pagina pubblicitaria "DABomb magazine" 2003
                  </p>
                </div>
              </DialogTrigger>
              <DialogContent className="bg-gray-900 border-gray-700 max-w-[95vw] sm:max-w-4xl p-2">
                <DialogTitle className="sr-only">Pagina pubblicitaria DABomb magazine 2003</DialogTitle>
                <img
                  src="https://customer-assets.emergentagent.com/job_site-mirror-165/artifacts/1ee2k8qj_primo%20flyer%20del%20tecniche.jpeg"
                  alt='Pagina pubblicitaria su "DABomb magazine" 2003'
                  className="w-full rounded-lg"
                />
                <p className="text-white text-center mt-2 pb-2 text-sm">Pagina pubblicitaria su "DABomb magazine" — 2003</p>
              </DialogContent>
            </Dialog>
          </div>

          {/* Sezione: SECONDO LOGO DEL T.P. */}
          <div className="border-t border-gray-800 pt-8 mt-2">
            <h3 className="text-2xl font-bold mb-2 text-red-400" style={{ fontFamily: 'Oswald, sans-serif' }}>
              SECONDO LOGO DEL T.P.
            </h3>
            <div className="w-16 h-1 mb-5" style={{ backgroundColor: 'var(--tp-red)' }} />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <Dialog>
                <DialogTrigger asChild>
                  <img
                    src="https://customer-assets.emergentagent.com/job_site-mirror-165/artifacts/390yxy4g_555046000_24879034851716023_7833020431395169509_n.jpg"
                    alt="Secondo logo Tecniche Perfette"
                    className="w-full aspect-square object-cover rounded-lg cursor-pointer hover:scale-105 transition-transform duration-300 border border-gray-700"
                  />
                </DialogTrigger>
                <DialogContent className="bg-gray-900 border-gray-700 max-w-[95vw] sm:max-w-3xl p-2">
                  <DialogTitle className="sr-only">Secondo logo Tecniche Perfette</DialogTitle>
                  <img
                    src="https://customer-assets.emergentagent.com/job_site-mirror-165/artifacts/390yxy4g_555046000_24879034851716023_7833020431395169509_n.jpg"
                    alt="Secondo logo Tecniche Perfette"
                    className="w-full rounded-lg"
                  />
                  <p className="text-white text-center mt-2 pb-2 text-sm">Secondo logo Tecniche Perfette — design BUD / Da Giantz</p>
                </DialogContent>
              </Dialog>

              <Dialog>
                <DialogTrigger asChild>
                  <img
                    src="https://customer-assets.emergentagent.com/job_site-mirror-165/artifacts/ypt8f380_471089031_564479173218106_4593905667026412800_n.jpg"
                    alt="Gianmarco Annecchini aka BUD"
                    className="w-full aspect-square object-cover rounded-lg cursor-pointer hover:scale-105 transition-transform duration-300 border border-gray-700"
                  />
                </DialogTrigger>
                <DialogContent className="bg-gray-900 border-gray-700 max-w-[95vw] sm:max-w-3xl p-2">
                  <DialogTitle className="sr-only">Gianmarco Annecchini aka BUD</DialogTitle>
                  <img
                    src="https://customer-assets.emergentagent.com/job_site-mirror-165/artifacts/ypt8f380_471089031_564479173218106_4593905667026412800_n.jpg"
                    alt="Gianmarco Annecchini aka BUD"
                    className="w-full rounded-lg"
                  />
                  <p className="text-white text-center mt-2 pb-2 text-sm">Gianmarco Annecchini aka BUD aka BiggaGia2 (Da Giantz)</p>
                </DialogContent>
              </Dialog>
            </div>

            <p className="text-gray-300 leading-relaxed">
              <strong className="text-white">Gianmarco Annecchini</strong> aka <strong className="text-red-400">BUD</strong> aka <strong className="text-red-400">BiggaGia2</strong>, è un rapper, grafico membro della crew{' '}
              <strong className="text-white">"Da Giantz"</strong>. Oltre ad esibirsi alla prima finale del 2003, ha realizzato un suo album
              solista e più volte ha collaborato ad alcune grafiche dei flyer della prima decade del Tecniche.
            </p>
          </div>

          {/* Sezione: PASSA IL MICROFONO */}
          <div className="border-t border-gray-800 pt-8 mt-2" data-testid="logo-passa-il-microfono">
            <h3 className="text-2xl font-bold mb-1 text-red-400" style={{ fontFamily: 'Oswald, sans-serif' }}>
              PASSA IL MICROFONO
            </h3>
            <p className="text-gray-400 text-sm italic mb-2">Quando il Tecniche si fermò per un'edizione</p>
            <div className="w-16 h-1 mb-5" style={{ backgroundColor: 'var(--tp-red)' }} />

            <Dialog>
              <DialogTrigger asChild>
                <img
                  src="https://customer-assets-0z36b82j.emergentagent.net/job_site-mirror-165/artifacts/74le6yy0_logo.png"
                  alt="Logo Passa il Microfono"
                  className="w-full object-contain bg-white rounded-lg cursor-pointer hover:scale-[1.02] transition-transform duration-300 border border-gray-700 p-3 mb-6"
                />
              </DialogTrigger>
              <DialogContent className="bg-gray-900 border-gray-700 max-w-[95vw] sm:max-w-4xl p-2">
                <DialogTitle className="sr-only">Logo Passa il Microfono</DialogTitle>
                <img
                  src="https://customer-assets-0z36b82j.emergentagent.net/job_site-mirror-165/artifacts/74le6yy0_logo.png"
                  alt="Logo Passa il Microfono"
                  className="w-full rounded-lg bg-white p-3"
                />
                <p className="text-white text-center mt-2 pb-2 text-sm">Passa il Microfono — logo realizzato da Mastafive, particolari di Lefty e Dragwan</p>
              </DialogContent>
            </Dialog>

            <p className="text-gray-300 leading-relaxed mb-6">
              Il logo fu realizzato da <strong className="text-red-400">Mastafive</strong>, con il contributo di{' '}
              <strong className="text-white">Lefty</strong> (Michele Alberti) e <strong className="text-white">Dragwan</strong> (Salvatore Calandra),
              che ne elaborarono i particolari.
            </p>

            <div className="grid grid-cols-2 gap-4 max-w-md">
              {[
                { name: 'Lefty', real: 'Michele Alberti', src: 'https://customer-assets-0z36b82j.emergentagent.net/job_site-mirror-165/artifacts/3nr9mko0_lefty%20.jpeg' },
                { name: 'Dragwan', real: 'Salvatore Calandra', src: 'https://customer-assets-0z36b82j.emergentagent.net/job_site-mirror-165/artifacts/x2tfbaj3_dragwan%20.jpeg' },
              ].map((p) => (
                <Dialog key={p.name}>
                  <DialogTrigger asChild>
                    <div className="cursor-pointer group" data-testid={`passa-microfono-${p.name.toLowerCase()}`}>
                      <p className="text-center mb-2">
                        <span className="block text-white font-bold text-sm" style={{ fontFamily: 'Oswald, sans-serif' }}>{p.name}</span>
                        <span className="block text-gray-400 text-xs">{p.real}</span>
                      </p>
                      <img
                        src={p.src}
                        alt={`${p.name} — ${p.real}`}
                        className="w-full aspect-square object-cover rounded-lg border border-gray-700 group-hover:border-red-500 group-hover:scale-105 transition-all duration-300"
                      />
                    </div>
                  </DialogTrigger>
                  <DialogContent className="bg-gray-900 border-gray-700 max-w-[95vw] sm:max-w-2xl p-2">
                    <DialogTitle className="sr-only">{p.name} — {p.real}</DialogTitle>
                    <img src={p.src} alt={`${p.name} — ${p.real}`} className="w-full rounded-lg" />
                    <p className="text-white text-center mt-2 pb-2 text-sm">{p.name} — {p.real}</p>
                  </DialogContent>
                </Dialog>
              ))}
            </div>
          </div>
          {/* Sezione: TERZO LOGO DEL T.P. */}
          <div className="border-t border-gray-800 pt-8 mt-2">
            <h3 className="text-2xl font-bold mb-2 text-red-400" style={{ fontFamily: 'Oswald, sans-serif' }}>
              TERZO LOGO DEL T.P.
            </h3>
            <div className="w-16 h-1 mb-5" style={{ backgroundColor: 'var(--tp-red)' }} />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <Dialog>
                <DialogTrigger asChild>
                  <img
                    src="https://customer-assets.emergentagent.com/job_site-mirror-165/artifacts/ebl109sc_327201533_1203536993881389_7747851404556065749_n.jpeg"
                    alt="Terzo logo Tecniche Perfette"
                    className="w-full aspect-square object-cover rounded-lg cursor-pointer hover:scale-105 transition-transform duration-300 border border-gray-700"
                  />
                </DialogTrigger>
                <DialogContent className="bg-gray-900 border-gray-700 max-w-[95vw] sm:max-w-3xl p-2">
                  <DialogTitle className="sr-only">Terzo logo Tecniche Perfette</DialogTitle>
                  <img
                    src="https://customer-assets.emergentagent.com/job_site-mirror-165/artifacts/ebl109sc_327201533_1203536993881389_7747851404556065749_n.jpeg"
                    alt="Terzo logo Tecniche Perfette"
                    className="w-full rounded-lg"
                  />
                  <p className="text-white text-center mt-2 pb-2 text-sm">Terzo logo Tecniche Perfette — design DAWLAZ</p>
                </DialogContent>
              </Dialog>

              <Dialog>
                <DialogTrigger asChild>
                  <img
                    src="https://customer-assets.emergentagent.com/job_site-mirror-165/artifacts/bf1yd1k6_470473726_122203955552200185_1610339168901951927_n.jpg"
                    alt="Davide Brusa aka DAWLAZ"
                    className="w-full aspect-square object-cover rounded-lg cursor-pointer hover:scale-105 transition-transform duration-300 border border-gray-700"
                  />
                </DialogTrigger>
                <DialogContent className="bg-gray-900 border-gray-700 max-w-[95vw] sm:max-w-3xl p-2">
                  <DialogTitle className="sr-only">Davide Brusa aka DAWLAZ</DialogTitle>
                  <img
                    src="https://customer-assets.emergentagent.com/job_site-mirror-165/artifacts/bf1yd1k6_470473726_122203955552200185_1610339168901951927_n.jpg"
                    alt="Davide Brusa aka DAWLAZ"
                    className="w-full rounded-lg"
                  />
                  <p className="text-white text-center mt-2 pb-2 text-sm">Davide Brusa aka DAWLAZ (Gate Keepaz)</p>
                </DialogContent>
              </Dialog>
            </div>

            <p className="text-gray-300 leading-relaxed">
              <strong className="text-white">Davide Brusa</strong> in arte <strong className="text-red-400">DAWLAZ</strong> è il
              creatore del terzo logo di Tecniche Perfette, è un graphic designer, rapper membro di{' '}
              <strong className="text-white">Gate Keepaz crew</strong>, ha realizzato diverse copertine di singoli ed album di
              moltissimi artisti tra cui <span className="text-white">Johnny Marsiglia, Rise Beatbox, Hardworkers, Maury B,
              B.M.Records, Mastafive</span> e naturalmente <span className="text-white">Gate Keepaz</span>.
              È stato ed è uno dei grafici ufficiali che segue le grafiche del Tecniche Perfette.
            </p>
          </div>

          {/* Sezione: QUARTO LOGO DEL T.P. */}
          <div className="border-t border-gray-800 pt-8 mt-8">
            <h3 className="text-2xl font-bold mb-2 text-red-400" style={{ fontFamily: 'Oswald, sans-serif' }}>
              QUARTO LOGO DEL T.P.
            </h3>
            <div className="w-16 h-1 mb-5" style={{ backgroundColor: 'var(--tp-red)' }} />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <Dialog>
                <DialogTrigger asChild>
                  <img
                    src="https://customer-assets.emergentagent.com/job_site-mirror-165/artifacts/uj7zfroc_63.png"
                    alt="Quarto logo Tecniche Perfette"
                    className="w-full aspect-square object-contain bg-black rounded-lg cursor-pointer hover:scale-105 transition-transform duration-300 border border-gray-700"
                  />
                </DialogTrigger>
                <DialogContent className="bg-gray-900 border-gray-700 max-w-[95vw] sm:max-w-3xl p-2">
                  <DialogTitle className="sr-only">Quarto logo Tecniche Perfette</DialogTitle>
                  <img
                    src="https://customer-assets.emergentagent.com/job_site-mirror-165/artifacts/uj7zfroc_63.png"
                    alt="Quarto logo Tecniche Perfette"
                    className="w-full rounded-lg bg-black"
                  />
                  <p className="text-white text-center mt-2 pb-2 text-sm">Quarto logo (attuale) Tecniche Perfette — restyling SNAKE</p>
                </DialogContent>
              </Dialog>

              <Dialog>
                <DialogTrigger asChild>
                  <img
                    src="https://customer-assets.emergentagent.com/job_site-mirror-165/artifacts/321vri0v_494733926_24347889524800294_1650539539049276344_n.jpg"
                    alt="Serse Rossini aka SNAKE"
                    className="w-full aspect-square object-cover rounded-lg cursor-pointer hover:scale-105 transition-transform duration-300 border border-gray-700"
                  />
                </DialogTrigger>
                <DialogContent className="bg-gray-900 border-gray-700 max-w-[95vw] sm:max-w-3xl p-2">
                  <DialogTitle className="sr-only">Serse Rossini aka SNAKE</DialogTitle>
                  <img
                    src="https://customer-assets.emergentagent.com/job_site-mirror-165/artifacts/321vri0v_494733926_24347889524800294_1650539539049276344_n.jpg"
                    alt="Serse Rossini aka SNAKE"
                    className="w-full rounded-lg"
                  />
                  <p className="text-white text-center mt-2 pb-2 text-sm">Serse Rossini aka SNAKE (Vikings FC)</p>
                </DialogContent>
              </Dialog>
            </div>

            <p className="text-gray-300 leading-relaxed">
              <strong className="text-white">Serse Rossini</strong> è conosciuto ai più come <strong className="text-red-400">SNAKE</strong>,
              fondatore della crew <strong className="text-white">Vikings FC</strong>, grafico e SMM. Ha modificato il terzo logo
              rendendolo attuale e utilizzabile su una nuova comunicazione che cura ormai da 6 anni sui profili e sulle grafiche
              ufficiali del T.P.
            </p>
          </div>

          {/* Sezione: WEBMASTERS */}
          <div className="border-t border-gray-800 pt-8 mt-8">
            <h3 className="text-2xl font-bold mb-2 text-red-400" style={{ fontFamily: 'Oswald, sans-serif' }}>
              WEBMASTERS
            </h3>
            <div className="w-16 h-1 mb-5" style={{ backgroundColor: 'var(--tp-red)' }} />

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <Dialog>
                <DialogTrigger asChild>
                  <img
                    src="https://customer-assets.emergentagent.com/job_site-mirror-165/artifacts/79tsphq1_44428310_10217332080031912_9013878727750189056_n.jpg"
                    alt="Umberto Greco aka Cane Bullo"
                    className="w-full aspect-square object-cover rounded-lg cursor-pointer hover:scale-105 transition-transform duration-300 border border-gray-700"
                  />
                </DialogTrigger>
                <DialogContent className="bg-gray-900 border-gray-700 max-w-[95vw] sm:max-w-3xl p-2">
                  <DialogTitle className="sr-only">Umberto Greco aka Cane Bullo</DialogTitle>
                  <img
                    src="https://customer-assets.emergentagent.com/job_site-mirror-165/artifacts/79tsphq1_44428310_10217332080031912_9013878727750189056_n.jpg"
                    alt="Umberto Greco aka Cane Bullo"
                    className="w-full rounded-lg"
                  />
                  <p className="text-white text-center mt-2 pb-2 text-sm">Umberto Greco aka Cane Bullo</p>
                </DialogContent>
              </Dialog>
            </div>

            <p className="text-gray-300 leading-relaxed">
              Il primissimo webmaster che ha curato i database, l'HTML, il Java e le programmazioni del Tecniche Perfette è stato{' '}
              <strong className="text-white">Umberto Greco</strong>, al secolo conosciuto come <strong className="text-red-400">Cane Bullo</strong>,
              rapper torinese che ha militato in alcuni collettivi tra cui <strong className="text-white">Da Giantz</strong>,{' '}
              <strong className="text-white">We Are D'Amore</strong>... ancora oggi attivo.
            </p>

            {/* Screenshot del primo sito Tecniche Perfette */}
            <div className="mt-6">
              <Dialog>
                <DialogTrigger asChild>
                  <img
                    src="https://customer-assets.emergentagent.com/job_site-mirror-165/artifacts/s5o6jjjw_Screenshot%202026-04-28%20alle%2003.11.05.png"
                    alt="Screenshot del primo sito Tecniche Perfette"
                    className="w-full max-w-3xl mx-auto rounded-lg cursor-pointer hover:scale-[1.02] transition-transform duration-300 border border-gray-700 block"
                  />
                </DialogTrigger>
                <DialogContent className="bg-gray-900 border-gray-700 max-w-[95vw] sm:max-w-5xl p-2">
                  <DialogTitle className="sr-only">Screenshot del primo sito Tecniche Perfette</DialogTitle>
                  <img
                    src="https://customer-assets.emergentagent.com/job_site-mirror-165/artifacts/s5o6jjjw_Screenshot%202026-04-28%20alle%2003.11.05.png"
                    alt="Screenshot del primo sito Tecniche Perfette"
                    className="w-full rounded-lg"
                  />
                  <p className="text-white text-center mt-2 pb-2 text-sm">
                    Screenshot del primo sito Tecniche Perfette — sviluppato da Cane Bullo
                  </p>
                </DialogContent>
              </Dialog>
              <p className="text-center text-gray-400 text-xs italic mt-2">
                Screenshot storico del primo sito Tecniche Perfette
              </p>
            </div>
          </div>
        </>
      )
    },
    {
      id: 'freestyle',
      title: "SIGNIFICATO DI FREESTYLE",
      image: "https://static.wixstatic.com/media/e93d0f_da82cfbd1b434200a929d76221103489~mv2.jpg/v1/fill/w_400,h_300,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/e93d0f_da82cfbd1b434200a929d76221103489~mv2.jpg",
      shortDesc: "La storia e il significato del Freestyle nel rap",
      fullContent: (
        <>
          <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Oswald, sans-serif' }}>COS'È IL FREESTYLE</h3>
          <p className="text-gray-300 mb-4 leading-relaxed">
            Il Freestyle è lo stile libero, lo dice la parola stessa. Significa che l'M.C. non è tenuto a seguire una linea di esposizione o intrattenimento, può decidere se inventarsi sul momento le liriche, le rime, eseguire rime dal suo repertorio, rime scritte appositamente per la battle (comunemente chiamate Punch Line), fare del Toasting.
          </p>
          <h4 className="text-xl font-bold mt-6 mb-3" style={{ fontFamily: 'Oswald, sans-serif' }}>Il rap che si improvvisa</h4>
          <p className="text-gray-400 text-sm mb-4 italic">
            Le battaglie di "freestyle" sono vecchie quanto l'hip hop, e continuano a rappresentare una delle sue forme più apprezzate dagli appassionati
          </p>
          <p className="text-gray-400 text-sm mb-4">di Giuseppe Luca Scaffidi</p>
          <p className="text-gray-300 mb-4 leading-relaxed">
            Il freestyle è uno degli elementi della cultura hip hop maggiormente radicati nell'immaginario collettivo. Negli anni ha rappresentato il punto di inizio della carriera di molti rapper, che in molti casi hanno imparato a padroneggiare questa tecnica prima ancora di iniziare a scrivere canzoni vere e proprie. Due casi tra i tantissimi sono quelli dei newyorkesi Jay–Z e Notorius B.I.G., che cominciarono a farsi notare nell'ambiente hip hop della East Coast statunitense improvvisando per strada o esibendosi per pochi minuti in una delle tante serate che venivano organizzate nei club della città.
          </p>
          <p className="text-gray-300 mb-4 leading-relaxed">
            Quando questo genere non aveva ancora un mercato così esteso, il freestyle svolse anche una funzione sociale. Le persone che si riconoscevano in questa sottocultura avevano bisogno di incontrarsi, e per farlo si davano appuntamento in punti precisi della città. A New York, la città in cui questo genere nacque agli inizi degli anni Settanta, chi voleva imparare a rappare di solito andava in posti come il Cedar, un parco pubblico del quartiere del Bronx, e l'Holcombe Rucker Park, un campo di pallacanestro di Harlem che ebbe una certa importanza nella diffusione di questa musica.
          </p>
          <p className="text-gray-300 leading-relaxed">
            Oggi battaglie di freestyle sono eventi ampiamente diffusi in tutto il mondo. Anche in Italia esistono diversi eventi specificamente dedicati a questa disciplina. Il più famoso si chiama "Tecniche perfette", fu inaugurato nel 2003 dai rapper torinesi DJ Double S (Rino Costante) e Mastafive (Johnny Andrea Mastrocinque), e da allora si è svolto ogni anno. "Tecniche perfette" ha aperto la strada alla carriera di altri rapper che, inizialmente, diventarono famosi soprattutto per il loro talento nell'improvvisazione, come Emis Killa e Clementino.
          </p>

          {/* Video embed Vimeo */}
          <div className="mt-8 rounded-lg overflow-hidden border border-gray-700" style={{ backgroundColor: 'rgba(0,0,0,0.4)' }}>
            <div className="px-4 py-3 border-b border-gray-700">
              <h5 className="text-base font-bold text-red-400" style={{ fontFamily: 'Oswald, sans-serif' }}>
                GUARDA IL VIDEO
              </h5>
            </div>
            <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
              <iframe
                src="https://player.vimeo.com/video/144754778"
                title="Significato di Freestyle"
                data-testid="freestyle-vimeo"
                className="absolute top-0 left-0 w-full h-full"
                style={{ border: 0 }}
                allow="autoplay; fullscreen; picture-in-picture; clipboard-write"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
              />
            </div>
          </div>
        </>
      ),
      teaserVimeoId: '144754778'
    },
    {
      id: 'roulette',
      title: "ROULETTE RUSSA",
      image: "https://static.wixstatic.com/media/e93d0f_59fe5429f0ed49d59857806e86a2f865~mv2.jpg/v1/fill/w_400,h_300,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/e93d0f_59fe5429f0ed49d59857806e86a2f865~mv2.jpg",
      shortDesc: "La fase di presentazione e selezione degli MC",
      fullContent: (
        <>
          <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Oswald, sans-serif' }}>ROULETTE RUSSA</h3>
          <p className="text-gray-300 mb-4 leading-relaxed">
            Il Top Of The Head è la capacità di improvvisare liriche e rime con attinenza a ciò che circonda l'M.C. e il luogo in cui è.
          </p>
          <p className="text-gray-300 mb-4 leading-relaxed">
            Significa "Dalla Testa", non prevede quindi repertorio ma solo attinenza al momento che si sta vivendo, all'argomento selezionato\proposto, a quello che uno vede nell'istante stesso in cui sta esponendo le sue rime.
          </p>
          <p className="text-gray-300 leading-relaxed">
            Il grado di difficoltà è elevato rispetto ad un normale freestyle perché fa leva sul bagaglio culturale e cognitivo dell'individuo e non solo su frasi ad effetto studiate ed elaborate.
          </p>
        </>
      )
    },
    {
      id: 'stile-libero',
      title: "STILE LIBERO",
      image: "https://customer-assets.emergentagent.com/job_site-mirror-165/artifacts/x197anxm_481076048_947437510895380_7943842358567613249_n.jpg",
      imagePosition: 'center 30%',
      shortDesc: "La modalità classica del freestyle: tre set, votazione pubblico e giuria",
      fullContent: (
        <>
          <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Oswald, sans-serif' }}>STILE LIBERO</h3>
          <p className="text-gray-300 leading-relaxed mb-4">
            <strong className="text-white">Stile libero</strong>, lo dice la parola stessa: i partecipanti hanno a disposizione <strong className="text-red-400">un minuto in 4/4</strong> per un massimo di <strong className="text-white">tre set</strong>. 
          </p>
          <p className="text-gray-300 leading-relaxed mb-4">
            Ad ogni set si procede alla <strong className="text-white">votazione</strong> sia del <strong className="text-red-400">pubblico</strong> che della <strong className="text-red-400">giuria</strong>. Se al secondo set viene confermato lo stesso vincitore, viene dichiarato <strong className="text-white">vincitore senza ulteriori sfide</strong>.
          </p>
          <p className="text-gray-300 leading-relaxed">
            In caso di <strong className="text-white">pareggio</strong>, si procederà al <strong className="text-red-400">terzo set</strong>.
          </p>
        </>
      )
    },
    {
      id: 'top-head',
      title: "TOP OF THE HEAD",
      image: "https://customer-assets.emergentagent.com/job_site-mirror-165/artifacts/siywaewe_hq720.jpg",
      shortDesc: "L'arte di improvvisare dal momento presente",
      fullContent: (
        <>
          <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Oswald, sans-serif' }}>TOP OF THE HEAD</h3>
          <p className="text-gray-300 mb-4 leading-relaxed">
            Il Top Of The Head è la capacità di improvvisare liriche e rime con attinenza a ciò che circonda l'M.C. e il luogo in cui è.
          </p>
          <p className="text-gray-300 mb-4 leading-relaxed">
            Significa "Dalla Testa", non prevede quindi repertorio ma solo attinenza al momento che si sta vivendo, all'argomento selezionato\proposto, a quello che uno vede nell'istante stesso in cui sta esponendo le sue rime.
          </p>
          <p className="text-gray-300 leading-relaxed">
            Il grado di difficoltà è elevato rispetto ad un normale freestyle perché fa leva sul bagaglio culturale e cognitivo dell'individuo e non solo su frasi ad effetto studiate ed elaborate.
          </p>
        </>
      )
    },
    {
      id: 'secondo-ko',
      title: "SECONDO ROUND K.O.",
      image: "https://customer-assets.emergentagent.com/job_site-mirror-165/artifacts/g3oo87u8_71PZ-CLlQfL._UF1000%2C1000_QL80_.jpg",
      shortDesc: "La seconda chance per gli MC in battaglia",
      fullContent: (
        <>
          <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Oswald, sans-serif' }}>SECONDO ROUND K.O.</h3>
          <p className="text-gray-300 mb-4 leading-relaxed">
            Il secondo round K.O. prende il nome dall'omonimo pezzo di Canibus (Second Round K.O.), pezzo pregno di rime da battaglia nel quale l'artista dichiara che con le sue liriche al secondo round sei K.O. (prendendo spunto dal modus operato da Tyson negli anni migliori dei suoi incontri di boxe, presenta come ospite nella traccia in questione).
          </p>
          <p className="text-gray-300 mb-4 leading-relaxed">
            Viene utilizzata la stessa strumentale.
          </p>
          <p className="text-gray-300 leading-relaxed">
            In questa seconda Chance gli MC's avranno a disposizione un ulteriore minuto in quattro quarti (a volte con argomento se le skillz dei due pretendenti sono particolarmente elevate).
          </p>
          <p className="text-gray-300 leading-relaxed">
            Si parte a tempo al 3-2-1 (conto alla rovescia) del conduttore, chi parte sui 4\4 non finisce sui 4\4 dell'altro MC, il primo che perde il tempo non vince e torna a casa. Senza alcuna votazione.
          </p>
        </>
      )
    },
    {
      id: 'da-cypha',
      title: "DA CYPHA",
      image: "https://customer-assets.emergentagent.com/job_site-mirror-165/artifacts/fdy8u2fu_images%20%283%29.jpeg",
      shortDesc: "Il cerchio sacro dell'Hip Hop",
      fullContent: (
        <>
          <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Oswald, sans-serif' }}>DA CYPHA</h3>
          <p className="text-gray-300 leading-relaxed">
            Il "cypha" è il cuore sacro dell'hip hop: un cerchio di MC che improvvisano a turno. A differenza di falsi miti su origini messicane, il termine deriva dall'arabo <em>sifr</em> (zero), ripreso dalla 5 Percent Nation di New York. Rakim lo consacrò come i "360 gradi di consapevolezza": 120 di conoscenza, 120 di saggezza e 120 di comprensione. In questo anello perfetto senza inizio né fine, il rapper raggiunge la totalità spirituale e l'energia fluisce pura tra i presenti.
          </p>
        </>
      )
    },
    {
      id: 'giro-morte',
      title: "GIRO DELLA MORTE",
      image: "https://static.wixstatic.com/media/e93d0f_ce8afeb851fb47e7b745fa0af2a17e73~mv2.jpg/v1/fill/w_400,h_300,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/e93d0f_ce8afeb851fb47e7b745fa0af2a17e73~mv2.jpg",
      shortDesc: "La prova finale del Tecniche Perfette",
      fullContent: (
        <>
          <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Oswald, sans-serif' }}>GIRO DELLA MORTE</h3>
          <p className="text-gray-300 mb-4 leading-relaxed">
            Il giro della morte è la prova finale del Tecniche Perfette, è quando i due MC's vanno avanti finché uno dei due non si arrende, fino a che i due finalisti non cominciano a ripetersi e perdono colpi dando evidenti segnali di repertorio esaurito, fino a che non c'è un buon motivo per fermarli.
          </p>
          <p className="text-gray-300 leading-relaxed">
            Nasce dal concetto di Cypha, il cerchio, quello che hanno esportato gruppi come Freestyle Fellowship, Haereogliphycs e Souls of mischief attraverso i filmati dei primi anni 90 contagiando anche la nostra nazione. Il giro della morte veniva fatto ogni sera dai GateKeepaz ed era l'allenamento definitivo. Questo è quello che univa gli MC nelle piazze e nei parcheggi 10\20 anni orsono e lo fa tuttora dimostrando che concetti come "il cerchio" non hanno lingua, religione, colore ed età.
          </p>
        </>
      )
    },
    {
      id: 'maury-b',
      title: "MAURY B - IL VETERANO",
      image: "https://customer-assets.emergentagent.com/job_site-mirror-165/artifacts/h5dpifns_623367332_1627910451945329_9077330588506023222_n.jpg",
      imagePosition: 'center 55%',
      teaserVideoId: 'HxjuM9q6eLc',
      shortDesc: "L'ispiratore della Tecnica Perfetta, veterano di Torino e leggenda dei Gate Keepaz",
      fullContent: (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {/* Colonna Sinistra - Immagine */}
            <div className="relative h-80 rounded-lg overflow-hidden">
              <img 
                src="https://customer-assets.emergentagent.com/job_site-mirror-165/artifacts/h5dpifns_623367332_1627910451945329_9077330588506023222_n.jpg" 
                alt="MAURY B" 
                className="w-full h-full object-cover"
                style={{ objectPosition: 'center 30%' }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-4">
                <h3 className="text-2xl font-bold text-white mb-1" style={{ fontFamily: 'Oswald, sans-serif' }}>MAURY B</h3>
                <p className="text-red-400 text-sm font-semibold">Il Veterano di Torino</p>
              </div>
            </div>
            
            {/* Colonna Destra - Video YouTube */}
            <div className="relative h-80 rounded-lg overflow-hidden bg-black">
              <iframe
                className="w-full h-full"
                src="https://www.youtube.com/embed/OKoyUxYaUpQ"
                title="MAURY B - Video"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
          </div>

          <div className="bg-gray-800/30 p-4 rounded-lg border-l-4 border-red-400 mb-6">
            <p className="text-gray-200 italic leading-relaxed">
              <strong className="text-red-400">MAURY B</strong> è l'ispiratore della <strong className="text-white">TECNICA PERFETTA</strong> tramandata prima ai <strong className="text-white">Gate Keepaz</strong> che ne divennero a loro volta dei promotori. È a lui (insieme ad <strong className="text-white">Ango Sprite</strong>) a cui si deve un certo tipo di attitudine del gruppo e della crew.
            </p>
          </div>

          <h4 className="text-xl font-bold mt-6 mb-3 text-red-400" style={{ fontFamily: 'Oswald, sans-serif' }}>Il Veterano di Torino</h4>
          <p className="text-gray-300 mb-4 leading-relaxed">
            Nato e cresciuto a <strong className="text-white">Nichelino</strong>, nella periferia sud di Torino, <strong className="text-white">Maury B</strong> si avvicina alla cultura Hip Hop nei primi anni '90. La sua storia è indissolubilmente legata alla scena torinese, una delle più fertili d'Italia, dove stringe un sodalizio artistico storico con <strong className="text-white">Left Side (Lefty)</strong>.
          </p>

          <h4 className="text-xl font-bold mt-6 mb-3 text-red-400" style={{ fontFamily: 'Oswald, sans-serif' }}>Gli Inizi e i Gate Keepaz</h4>
          <p className="text-gray-300 mb-4 leading-relaxed">
            La sua ascesa inizia con la formazione del collettivo <strong className="text-white">Gate Keepaz</strong>, un gruppo leggendario che comprendeva nomi come <strong className="text-white">Left Side</strong>, <strong className="text-white">Lyricalz</strong>, e <strong className="text-white">Bassi Maestro</strong> (in alcune fasi). Con i Gate Keepaz, Maury B contribuisce a definire il suono "hardcore" e consapevole degli anni '90, partecipando a pietre miliari come <strong className="text-white">Dietro il cancello</strong> (1997).
          </p>

          <h4 className="text-xl font-bold mt-6 mb-3 text-red-400" style={{ fontFamily: 'Oswald, sans-serif' }}>La Maturità Artistica</h4>
          <p className="text-gray-300 mb-4 leading-relaxed">
            Famoso per il suo <strong className="text-white">timbro vocale profondo</strong> e una <strong className="text-white">metrica precisa e incisiva</strong>, Maury B è stato uno dei pochi artisti capaci di restare attivo e credibile per oltre tre decenni. Ha collaborato con i più grandi nomi della scena (da <strong className="text-white">Fritz Da Cat</strong> a <strong className="text-white">DJ Shocca</strong>) e ha sempre rivendicato il ruolo del rap come strumento di narrazione sociale e personale, lontano dalle logiche puramente commerciali.
          </p>
          <p className="text-gray-300 mb-4 leading-relaxed">
            Dopo un periodo di silenzio discografico come solista, è tornato con forza negli ultimi anni, dimostrando che il rap "classico" ha ancora un pubblico vasto e appassionato.
          </p>

          <h4 className="text-xl font-bold mt-6 mb-3 text-red-400" style={{ fontFamily: 'Oswald, sans-serif' }}>Curiosità</h4>
          <div className="bg-gray-800/50 p-4 rounded-lg mb-4">
            <p className="text-gray-300 mb-3 leading-relaxed">
              <strong className="text-white">Lo stile:</strong> È considerato uno dei massimi esponenti del rap <strong className="text-red-400">"Boom Bap"</strong> in Italia, influenzato pesantemente dalla Golden Age americana (<strong className="text-white">Nas</strong>, <strong className="text-white">Big Daddy Kane</strong>, <strong className="text-white">Rakim</strong>).
            </p>
            <p className="text-gray-300 leading-relaxed">
              <strong className="text-white">Il legame con il vinile:</strong> Fedele alla tradizione, quasi tutte le sue uscite importanti (inclusi i recenti lavori) sono state stampate in <strong className="text-red-400">formato vinile</strong> per i collezionisti e i puristi del genere.
            </p>
          </div>

          <p className="text-gray-300 leading-relaxed italic border-l-4 border-red-400 pl-4 py-2 bg-gray-800/30">
            Maury B rappresenta oggi un <strong className="text-white">ponte vivente</strong> tra la vecchia scuola e le nuove generazioni che cercano nel rap <strong className="text-red-400">tecnica, coerenza e "realness"</strong>.
          </p>

          {/* Video embed finale */}
          <div className="mt-8 rounded-lg overflow-hidden border border-gray-700" style={{ backgroundColor: 'rgba(0,0,0,0.4)' }}>
            <div className="px-4 py-3 border-b border-gray-700">
              <h5 className="text-base font-bold text-red-400" style={{ fontFamily: 'Oswald, sans-serif' }}>
                GUARDA IL VIDEO
              </h5>
            </div>
            <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
              <iframe
                src="https://www.youtube.com/embed/HxjuM9q6eLc"
                title="Maury B - Video"
                data-testid="maury-b-video"
                className="absolute top-0 left-0 w-full h-full"
                style={{ border: 0 }}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
              />
            </div>
          </div>
        </>
      )
    },
    {
      id: 'video-storia',
      title: "LA STORIA DI TECNICHE PERFETTE",
      image: "https://img.youtube.com/vi/P-zRSFdAQD4/maxresdefault.jpg",
      imagePosition: 'center',
      shortDesc: "Video documentario sulla storia del contest",
      fullContent: (
        <>
          <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Oswald, sans-serif' }}>LA STORIA DI TECNICHE PERFETTE</h3>
          <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
            <iframe 
              className="absolute top-0 left-0 w-full h-full rounded-lg"
              src="https://www.youtube.com/embed/P-zRSFdAQD4?si=NTkozmAfqqhj1HD-" 
              title="YouTube video player" 
              frameBorder="0" 
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" 
              referrerPolicy="strict-origin-when-cross-origin" 
              allowFullScreen
            ></iframe>
          </div>
        </>
      )
    },
    {
      id: 'born-mlist-mirc',
      title: "BORN by Mailing List & mIRC",
      image: "https://customer-assets.emergentagent.com/job_site-mirror-165/artifacts/4wjyry24_image.png",
      imageFit: 'cover',
      shortDesc: "Le radici del network T.P.: dalla mailing list di Ciobin alle chat IRC dell'epoca 56k",
      fullContent: (
        <>
          <h3 className="text-2xl font-bold mb-2 text-red-400" style={{ fontFamily: 'Oswald, sans-serif' }}>
            BORN BY MAILING LIST &amp; mIRC
          </h3>
          <div className="w-16 h-1 mb-5" style={{ backgroundColor: 'var(--tp-red)' }} />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-6">
            <div className="rounded-lg overflow-hidden border-2 border-red-600/60 bg-black/40">
              <img
                src="https://customer-assets.emergentagent.com/job_site-mirror-165/artifacts/4wjyry24_image.png"
                alt="Mailing list di Ciobin – archivio storico Tecniche Perfette"
                className="w-full h-auto object-contain"
                loading="lazy"
                data-testid="born-mirc-image-1"
              />
            </div>
            <div className="rounded-lg overflow-hidden border-2 border-red-600/60 bg-black/40">
              <img
                src="https://customer-assets.emergentagent.com/job_site-mirror-165/artifacts/ijnhh5fk_image.png"
                alt="Chat mIRC dell'epoca 56k – scene hip-hop italiane"
                className="w-full h-auto object-contain"
                loading="lazy"
                data-testid="born-mirc-image-2"
              />
            </div>
          </div>

          <p className="text-gray-300 leading-relaxed mb-4">
            L'organizzazione nelle varie regioni non è nata semplicemente dalla notorietà di{' '}
            <strong className="text-white">Mastafive</strong> e <strong className="text-white">Double S</strong>: al primo
            giro di contatti è stata fondamentale la <strong className="text-red-400">mailing list di Silvia Quartero</strong>,
            al secolo <strong className="text-white">Ciobin</strong>, tramite l'indirizzo{' '}
            <em className="text-gray-200">ciobin@email.it</em>, che raccoglieva le news e gli annunci di serate e jam di
            tutto lo stivale (aveva anche un bel giro clandestino di mixtape). Senza di lei sarebbe stato tutto molto più lento.
          </p>

          <p className="text-gray-300 leading-relaxed mb-4">
            Fondamentali sono stati anche i canali delle chat <strong className="text-red-400">IRC / mIRC / ICQ</strong> come{' '}
            <em className="text-gray-200">Hiphop.it</em> (divenuto poi un sito),{' '}
            <em className="text-gray-200">Hip_hop_italia</em>, <em className="text-gray-200">Hip Hop &amp; Stop</em>,{' '}
            <em className="text-gray-200">Stile</em>, <em className="text-gray-200">Josteddu</em>… canali tenuti su dai
            primi <strong className="text-white">BOT DOS</strong>, creati per tenerli occupati.
          </p>

          <p className="text-gray-300 leading-relaxed mb-4">
            Da lì sono nate moltissime connection, parallelamente alle <strong className="text-white">chat di MSN</strong>,
            dove si riusciva a passare file e fare video call in tempi in cui i <strong className="text-red-400">56k</strong>{' '}
            di connessione sembravano eterne guerre di tempo e bollette.
          </p>

          <p className="text-gray-300 leading-relaxed mb-6">
            Tutto accadeva tra il <strong className="text-white">1999</strong> e il <strong className="text-white">2003</strong>,
            anno in cui fece la sua apparizione sul web <strong className="text-white">MySpace</strong>.
          </p>

          <div className="border-l-4 border-red-600 pl-4 py-2 bg-red-900/10 rounded-r-lg">
            <p className="text-red-400 italic font-bold text-lg leading-snug" style={{ fontFamily: 'Oswald, sans-serif' }}>
              "La nuova classe dirigente si è formata sul 56k, non sulla fibra dei social."
            </p>
          </div>
        </>
      )
    }
  ];

  return (
    <div className="py-10 sm:py-16 px-4" style={{ backgroundColor: 'var(--tp-black)', minHeight: '80vh' }}>
      <div className="max-w-6xl mx-auto">
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-2"
          style={{ fontFamily: 'Bebas Neue, sans-serif', letterSpacing: '2px' }}>BIOGRAFIE</h1>
        <div className="w-20 h-1 mb-3" style={{ backgroundColor: 'var(--tp-red)' }} />
        <p className="text-gray-400 text-sm mb-8 sm:mb-10">
          La storia, le regole e i protagonisti di Tecniche Perfette.
        </p>

        {/* Founders Section */}
        <div className="mb-8 sm:mb-10">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4 flex items-center gap-2" style={{ fontFamily: 'Bebas Neue, sans-serif', letterSpacing: '1px' }}>
            <Crown size={24} className="text-yellow-600" />
            FOUNDERS
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-8">
            {founders.map((founder) => (
              <FounderCard key={founder.id} founder={founder} />
            ))}
          </div>
        </div>

        {/* Host Ufficiali Section */}
        <div className="mb-8 sm:mb-12">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4 flex items-center gap-2" style={{ fontFamily: 'Bebas Neue, sans-serif', letterSpacing: '1px' }}>
            <Mic size={24} className="text-red-500" />
            HOST UFFICIALI
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            {hosts.map((host) => (
              <Dialog key={host.id}>
                <DialogTrigger asChild>
                  <div className="rounded-lg overflow-hidden border-2 border-red-600 hover:border-red-400 transition-all duration-300 cursor-pointer group"
                    style={{ backgroundColor: 'var(--tp-dark)' }}>
                    <div className="h-48 sm:h-56 relative overflow-hidden">
                      <img 
                        src={host.image} 
                        alt={host.name} 
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" 
                        style={
                          host.id === 'kiave' ? { objectPosition: 'center 20%' } :
                          host.id === 'joelz' ? { objectPosition: 'center 25%' } :
                          host.id === 'rido-mc' ? { objectPosition: 'center 30%' } :
                          host.id === 'emis-killa' ? { objectPosition: 'center 20%' } : {}
                        }
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
                      <div className="absolute top-3 right-3 px-2 py-1 rounded-full bg-red-600">
                        <Mic size={14} className="text-white" />
                      </div>
                      <div className="absolute bottom-3 left-3 right-3">
                        <h3 className="text-xl sm:text-2xl font-bold text-white mb-1" style={{ fontFamily: 'Bebas Neue, sans-serif', letterSpacing: '1px' }}>
                          {host.name}
                        </h3>
                        {host.realName && host.realName !== host.name && (
                          <p className="text-red-400 text-xs font-bold mb-1" style={{ fontFamily: 'Oswald, sans-serif' }}>
                            {host.realName}
                          </p>
                        )}
                        <p className="text-gray-300 text-xs">{host.role}</p>
                      </div>
                    </div>
                    <div className="p-3 sm:p-4">
                      <p className="text-gray-400 text-xs sm:text-sm">{host.shortBio}</p>
                    </div>
                  </div>
                </DialogTrigger>
                <DialogContent className="bg-gray-900 border-gray-700 max-w-[95vw] sm:max-w-3xl max-h-[90vh] overflow-y-auto">
                  <DialogTitle className="sr-only">{host.name}</DialogTitle>
                  <DialogDescription className="sr-only">Host ufficiale Tecniche Perfette</DialogDescription>
                  <div className="space-y-4">
                    <div className="relative h-48 sm:h-64 rounded-lg overflow-hidden">
                      <img 
                        src={host.image} 
                        alt={host.name} 
                        className="w-full h-full object-cover" 
                        style={
                          host.id === 'kiave' ? { objectPosition: 'center 20%' } :
                          host.id === 'joelz' ? { objectPosition: 'center 25%' } :
                          host.id === 'rido-mc' ? { objectPosition: 'center 30%' } :
                          host.id === 'emis-killa' ? { objectPosition: 'center 20%' } : {}
                        }
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                      <div className="absolute bottom-4 left-4 right-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Mic size={20} className="text-red-500" />
                          <span className="text-red-500 font-bold text-xs" style={{ fontFamily: 'Oswald, sans-serif' }}>HOST UFFICIALE</span>
                        </div>
                        <h2 className="text-3xl sm:text-4xl font-bold text-white mb-1" style={{ fontFamily: 'Bebas Neue, sans-serif', letterSpacing: '1px' }}>
                          {host.name}
                        </h2>
                        {host.realName && host.realName !== host.name && (
                          <p className="text-red-400 text-sm font-bold" style={{ fontFamily: 'Oswald, sans-serif' }}>
                            {host.realName}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    <div className="px-2 space-y-2 text-sm sm:text-base">
                      {host.fullBio}
                    </div>

                    {host.links && host.links.length > 0 && (
                      <div className="flex flex-wrap gap-3 pt-4 border-t border-gray-700">
                        {host.links.map((link, idx) => (
                          <a 
                            key={idx}
                            href={link.url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="px-4 py-2 rounded bg-red-600 hover:bg-red-700 text-white font-bold text-sm flex items-center gap-2 transition-colors"
                            style={{ fontFamily: 'Oswald, sans-serif' }}
                          >
                            {link.label}
                            <ExternalLink size={14} />
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                </DialogContent>
              </Dialog>
            ))}
          </div>
        </div>


        {/* DEE JAY'S UFFICIALI Section */}
        <div className="mb-8 sm:mb-12">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4 flex items-center gap-2" style={{ fontFamily: 'Bebas Neue, sans-serif', letterSpacing: '1px' }}>
            <Disc size={24} className="text-red-500" />
            DEE JAY'S UFFICIALI
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            {djs.map((dj) => (
              <DJCard key={dj.id} dj={dj} />
            ))}
          </div>
        </div>

        {/* CONTENT CREATOR Section */}
        <div className="mb-8 sm:mb-12">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4 flex items-center gap-2" style={{ fontFamily: 'Bebas Neue, sans-serif', letterSpacing: '1px' }}>
            <Video size={24} className="text-purple-500" />
            CONTENT CREATOR
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            {creators.map((creator) => (
              <CreatorCard key={creator.id} creator={creator} />
            ))}
          </div>
        </div>

        {/* PROJECT MANAGERS Section */}
        <div className="mb-8 sm:mb-12">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4 flex items-center gap-2" style={{ fontFamily: 'Bebas Neue, sans-serif', letterSpacing: '1px' }}>
            <Trophy size={24} className="text-red-500" />
            PROJECT MANAGERS
          </h2>
          <p className="text-gray-400 text-sm sm:text-base mb-6 max-w-3xl leading-relaxed">
            Negli anni si sono susseguiti molti project manager, ma alcuni di loro sono stati fondamentali per la riuscita degli eventi finali e semifinali.
          </p>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 gap-3">
            {/* Stefano Bolognesi */}
            <div className="rounded-lg border border-gray-800 hover:border-red-600 transition-all bg-gray-900 p-2.5 flex flex-col">
              <Dialog>
                <DialogTrigger asChild>
                  <img
                    src="https://customer-assets.emergentagent.com/job_site-mirror-165/artifacts/z6xl3lod_506150617_10232068340024868_3968551846491285027_n.jpg"
                    alt="Stefano Bolognesi"
                    className="w-14 h-14 rounded-full mx-auto object-cover cursor-pointer hover:scale-105 transition-transform duration-300 border-2 border-gray-700"
                    style={{ filter: 'grayscale(100%)' }}
                  />
                </DialogTrigger>
                <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-[95vw] sm:max-w-2xl p-2">
                  <DialogTitle className="sr-only">Stefano Bolognesi</DialogTitle>
                  <img src="https://customer-assets.emergentagent.com/job_site-mirror-165/artifacts/z6xl3lod_506150617_10232068340024868_3968551846491285027_n.jpg" alt="Stefano Bolognesi" className="w-full rounded-lg" />
                </DialogContent>
              </Dialog>
              <h3 className="text-white font-bold text-xs text-center mt-2 leading-tight" style={{ fontFamily: 'Oswald, sans-serif' }}>Stefano Bolognesi</h3>
              <p className="text-red-400 text-[10px] text-center mb-2 leading-tight" style={{ fontFamily: 'Oswald, sans-serif' }}>Producer · B.M.Records</p>
              <p className="text-gray-300 text-[11px] leading-snug">
                Il primo in assoluto, uno stacanovista e produttore (ha prodotto il primo singolo realizzato da{' '}
                <strong className="text-white">Clementino</strong> con <strong className="text-white">Malva</strong>).
                Fondatore tra l'altro di <strong className="text-red-400">B.M.Records</strong> insieme a{' '}
                <strong className="text-white">Mastafive</strong> e <strong className="text-white">Lefty</strong>.
              </p>
            </div>

            {/* Mattia Menegazzi */}
            <div className="rounded-lg border border-gray-800 hover:border-red-600 transition-all bg-gray-900 p-2.5 flex flex-col">
              <Dialog>
                <DialogTrigger asChild>
                  <img
                    src="https://customer-assets.emergentagent.com/job_site-mirror-165/artifacts/nob80qrx_470527937_10162734031670972_8763182834591046509_n.jpg"
                    alt="Mattia Menegazzi"
                    className="w-14 h-14 rounded-full mx-auto object-cover cursor-pointer hover:scale-105 transition-transform duration-300 border-2 border-gray-700"
                    style={{ filter: 'grayscale(100%)' }}
                  />
                </DialogTrigger>
                <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-[95vw] sm:max-w-2xl p-2">
                  <DialogTitle className="sr-only">Mattia Menegazzi</DialogTitle>
                  <img src="https://customer-assets.emergentagent.com/job_site-mirror-165/artifacts/nob80qrx_470527937_10162734031670972_8763182834591046509_n.jpg" alt="Mattia Menegazzi" className="w-full rounded-lg" />
                </DialogContent>
              </Dialog>
              <h3 className="text-white font-bold text-xs text-center mt-2 leading-tight" style={{ fontFamily: 'Oswald, sans-serif' }}>Mattia Menegazzi</h3>
              <p className="text-red-400 text-[10px] text-center mb-2 leading-tight" style={{ fontFamily: 'Oswald, sans-serif' }}>Producer · DJ · Beatmaker</p>
              <p className="text-gray-300 text-[11px] leading-snug">
                <strong className="text-white">Mattia Menegazzi</strong>, cresciuto in seno a{' '}
                <strong className="text-red-400">B.M.Records</strong> e <strong className="text-white">One Love Movement</strong>,
                è ora un affermato produttore musicale, DJ, beatmaker e ingegnere del suono originario della{' '}
                <strong className="text-white">Valle d'Aosta</strong>.
              </p>
            </div>

            {/* Gianluca Coppola */}
            <div className="rounded-lg border border-gray-800 hover:border-red-600 transition-all bg-gray-900 p-2.5 flex flex-col">
              <Dialog>
                <DialogTrigger asChild>
                  <img
                    src="https://customer-assets.emergentagent.com/job_site-mirror-165/artifacts/o597xm1b_517863448_10237442594872993_1776536128046157273_n.jpg"
                    alt="Gianluca Coppola"
                    className="w-14 h-14 rounded-full mx-auto object-cover cursor-pointer hover:scale-105 transition-transform duration-300 border-2 border-gray-700"
                    style={{ filter: 'grayscale(100%)' }}
                  />
                </DialogTrigger>
                <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-[95vw] sm:max-w-2xl p-2">
                  <DialogTitle className="sr-only">Gianluca Coppola</DialogTitle>
                  <img src="https://customer-assets.emergentagent.com/job_site-mirror-165/artifacts/o597xm1b_517863448_10237442594872993_1776536128046157273_n.jpg" alt="Gianluca Coppola" className="w-full rounded-lg" />
                </DialogContent>
              </Dialog>
              <h3 className="text-white font-bold text-xs text-center mt-2 leading-tight" style={{ fontFamily: 'Oswald, sans-serif' }}>Gianluca Coppola</h3>
              <p className="text-red-400 text-[10px] text-center mb-2 leading-tight" style={{ fontFamily: 'Oswald, sans-serif' }}>MAS Network Founder · Operatore Olistico</p>
              <p className="text-gray-300 text-[11px] leading-snug">
                <strong className="text-white">Gianluca Coppola</strong> fonda nel{' '}
                <strong className="text-white">2013</strong> la <strong className="text-red-400">MAS NETWORK</strong>, agenzia di
                consulenze artistiche, assorbita da <strong className="text-white">Mastafive</strong> nel{' '}
                <strong className="text-white">2016</strong>.
              </p>
              <p className="text-gray-300 text-[11px] leading-snug mt-1.5 italic">
                Il suo motto: <span className="text-red-400 not-italic font-bold">"Amma fa na cos'e sold!"</span>
              </p>
              <p className="text-gray-300 text-[11px] leading-snug mt-1.5">
                Ora è un <strong className="text-white">Operatore Olistico</strong>.
              </p>
            </div>

            {/* Lorenzo Bruno — er molla */}
            <div className="rounded-lg border border-gray-800 hover:border-red-600 transition-all bg-gray-900 p-2.5 flex flex-col">
              <Dialog>
                <DialogTrigger asChild>
                  <img
                    src="https://customer-assets.emergentagent.com/job_site-mirror-165/artifacts/t3xqvojo_117294083_314487009672332_7918886365212718740_n.jpg"
                    alt="Lorenzo Bruno aka er molla"
                    className="w-14 h-14 rounded-full mx-auto object-cover cursor-pointer hover:scale-105 transition-transform duration-300 border-2 border-gray-700"
                    style={{ filter: 'grayscale(100%)' }}
                  />
                </DialogTrigger>
                <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-[95vw] sm:max-w-2xl p-2">
                  <DialogTitle className="sr-only">Lorenzo Bruno aka er molla</DialogTitle>
                  <img src="https://customer-assets.emergentagent.com/job_site-mirror-165/artifacts/t3xqvojo_117294083_314487009672332_7918886365212718740_n.jpg" alt="Lorenzo Bruno" className="w-full rounded-lg" />
                </DialogContent>
              </Dialog>
              <h3 className="text-white font-bold text-xs text-center mt-2 leading-tight" style={{ fontFamily: 'Oswald, sans-serif' }}>Lorenzo Bruno</h3>
              <p className="text-red-400 text-[10px] text-center mb-2 leading-tight" style={{ fontFamily: 'Oswald, sans-serif' }}>"er molla" · Giornalista · Booking Agent</p>
              <p className="text-gray-300 text-[11px] leading-snug">
                <strong className="text-white">Lorenzo Bruno</strong>, <strong className="text-red-400">"er molla"</strong>,
                diligente giornalista e booking agent ma infognato di <strong className="text-white">Jujitsu</strong>: lo potete
                ammirare in questa foto alla Liberato.
              </p>
            </div>

            {/* Naty Paolone — Aiko Red */}
            <div className="rounded-lg border border-gray-800 hover:border-red-600 transition-all bg-gray-900 p-2.5 flex flex-col">
              <Dialog>
                <DialogTrigger asChild>
                  <img
                    src="https://customer-assets.emergentagent.com/job_site-mirror-165/artifacts/0mbvxa1r_474498344_2884620868544811_4792928644921013946_n.jpg"
                    alt="Naty Paolone aka Aiko Red"
                    className="w-14 h-14 rounded-full mx-auto object-cover cursor-pointer hover:scale-105 transition-transform duration-300 border-2 border-gray-700"
                    style={{ filter: 'grayscale(100%)' }}
                  />
                </DialogTrigger>
                <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-[95vw] sm:max-w-2xl p-2">
                  <DialogTitle className="sr-only">Naty Paolone aka Aiko Red</DialogTitle>
                  <img src="https://customer-assets.emergentagent.com/job_site-mirror-165/artifacts/0mbvxa1r_474498344_2884620868544811_4792928644921013946_n.jpg" alt="Naty Paolone" className="w-full rounded-lg" />
                </DialogContent>
              </Dialog>
              <h3 className="text-white font-bold text-xs text-center mt-2 leading-tight" style={{ fontFamily: 'Oswald, sans-serif' }}>Naty Paolone</h3>
              <p className="text-red-400 text-[10px] text-center mb-2 leading-tight" style={{ fontFamily: 'Oswald, sans-serif' }}>"Aiko Red" · Artist Consultant</p>
              <p className="text-gray-300 text-[11px] leading-snug">
                <strong className="text-white">Naty Paolone</strong>, al secolo <strong className="text-red-400">Aiko Red</strong>,
                ha coordinato molte date del Tecniche Perfette dal <strong className="text-white">2019</strong> ad oggi. Collabora
                con <strong className="text-red-400">MAS NETWORK</strong> e ha coordinato e diretto diversi format di{' '}
                <strong className="text-white">Radio For Music</strong>.
              </p>
            </div>

            {/* Giulia Villa — Julyetta */}
            <div className="rounded-lg border border-gray-800 hover:border-red-600 transition-all bg-gray-900 p-2.5 flex flex-col">
              <Dialog>
                <DialogTrigger asChild>
                  <img
                    src="https://customer-assets.emergentagent.com/job_site-mirror-165/artifacts/xsqrret1_514329965_24825024647095473_5537101174667926431_n.jpg"
                    alt="Giulia Villa aka Julyetta"
                    className="w-14 h-14 rounded-full mx-auto object-cover cursor-pointer hover:scale-105 transition-transform duration-300 border-2 border-gray-700"
                    style={{ filter: 'grayscale(100%)' }}
                  />
                </DialogTrigger>
                <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-[95vw] sm:max-w-2xl p-2">
                  <DialogTitle className="sr-only">Giulia Villa aka Julyetta</DialogTitle>
                  <img src="https://customer-assets.emergentagent.com/job_site-mirror-165/artifacts/xsqrret1_514329965_24825024647095473_5537101174667926431_n.jpg" alt="Giulia Villa" className="w-full rounded-lg" />
                </DialogContent>
              </Dialog>
              <h3 className="text-white font-bold text-xs text-center mt-2 leading-tight" style={{ fontFamily: 'Oswald, sans-serif' }}>Giulia Villa</h3>
              <p className="text-red-400 text-[10px] text-center mb-2 leading-tight" style={{ fontFamily: 'Oswald, sans-serif' }}>"Julyetta" · Promoter · Talent Scout</p>
              <p className="text-gray-300 text-[11px] leading-snug">
                <strong className="text-white">Giulia Villa</strong>, detta <strong className="text-red-400">Julyetta</strong>:
                promoter, manager, imprenditrice e talent scout. Si è unita a{' '}
                <strong className="text-red-400">MAS NETWORK</strong> dal <strong className="text-white">2023</strong>. Una mina
                vagante di creatività e stacanovismo.
              </p>
            </div>

            {/* Luigi Campagna */}
            <div className="rounded-lg border border-gray-800 hover:border-red-600 transition-all bg-gray-900 p-2.5 flex flex-col">
              <Dialog>
                <DialogTrigger asChild>
                  <img
                    src="https://customer-assets.emergentagent.com/job_site-mirror-165/artifacts/vkpvuz4h_59661416_10219304102984300_1975569769432088576_n.jpg"
                    alt="Luigi Campagna"
                    className="w-14 h-14 rounded-full mx-auto object-cover cursor-pointer hover:scale-105 transition-transform duration-300 border-2 border-gray-700"
                    style={{ filter: 'grayscale(100%)' }}
                  />
                </DialogTrigger>
                <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-[95vw] sm:max-w-2xl p-2">
                  <DialogTitle className="sr-only">Luigi Campagna</DialogTitle>
                  <img src="https://customer-assets.emergentagent.com/job_site-mirror-165/artifacts/vkpvuz4h_59661416_10219304102984300_1975569769432088576_n.jpg" alt="Luigi Campagna" className="w-full rounded-lg" />
                </DialogContent>
              </Dialog>
              <h3 className="text-white font-bold text-xs text-center mt-2 leading-tight" style={{ fontFamily: 'Oswald, sans-serif' }}>Luigi "Gigi" Campagna</h3>
              <p className="text-red-400 text-[10px] text-center mb-2 leading-tight" style={{ fontFamily: 'Oswald, sans-serif' }}>Promoter Milano & Brianza · Book Manager</p>
              <p className="text-gray-300 text-[11px] leading-snug">
                <strong className="text-white">Luigi Campagna</strong> è uno dei più famosi promoter di{' '}
                <strong className="text-white">Milano e Brianza</strong>: ha un accento meneghino da fare invidia ai Club Dogo, ma è{' '}
                <strong className="text-red-400">calabrese puro sangue</strong>. Da quasi 15 anni collabora con{' '}
                <strong className="text-red-400">MAS NETWORK</strong> e <strong className="text-white">Tecniche Perfette</strong>,
                realizzando alcune delle date più iconiche del T.P. ad oggi.
              </p>
            </div>

            {/* Enrico Pellegrini — Reset Lab */}
            <div className="rounded-lg border border-gray-800 hover:border-red-600 transition-all bg-gray-900 p-2.5 flex flex-col" data-testid="pm-enrico-pellegrini">
              <Dialog>
                <DialogTrigger asChild>
                  <img
                    src="https://customer-assets-0z36b82j.emergentagent.net/job_site-mirror-165/artifacts/86opfd0m_IMG_6344.jpeg"
                    alt="Enrico Pellegrini"
                    className="w-14 h-14 rounded-full mx-auto object-cover cursor-pointer hover:scale-105 transition-transform duration-300 border-2 border-gray-700"
                    style={{ filter: 'grayscale(100%)' }}
                  />
                </DialogTrigger>
                <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-[95vw] sm:max-w-2xl p-2">
                  <DialogTitle className="sr-only">Enrico Pellegrini</DialogTitle>
                  <img src="https://customer-assets-0z36b82j.emergentagent.net/job_site-mirror-165/artifacts/86opfd0m_IMG_6344.jpeg" alt="Enrico Pellegrini" className="w-full rounded-lg" />
                </DialogContent>
              </Dialog>
              <h3 className="text-white font-bold text-xs text-center mt-2 leading-tight" style={{ fontFamily: 'Oswald, sans-serif' }}>Enrico Pellegrini</h3>
              <p className="text-red-400 text-[10px] text-center mb-2 leading-tight" style={{ fontFamily: 'Oswald, sans-serif' }}>Manager · Reset Lab Founder</p>
              <p className="text-gray-300 text-[11px] leading-snug">
                Classe <strong className="text-white">1994</strong>, nasce a <strong className="text-white">Montepulciano</strong> e vive a{' '}
                <strong className="text-white">Milano</strong>. Manager e professionista del music business, è fondatore di{' '}
                <strong className="text-red-400">Reset Lab</strong>, agenzia specializzata nello sviluppo artistico e strategico di progetti musicali.
              </p>
              <p className="text-gray-300 text-[11px] leading-snug mt-1.5">
                Attivo soprattutto nella scena <strong className="text-white">rap, urban e freestyle</strong> italiana, affianca artisti affermati e
                nuovi talenti nella costruzione di identità, percorsi discografici e opportunità live, unendo visione creativa, comunicazione e strategia.
              </p>
            </div>
          </div>
        </div>

        {/* Storia Section */}
        <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4" style={{ fontFamily: 'Bebas Neue, sans-serif', letterSpacing: '1px' }}>
          STORIA
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-8 sm:mb-10">
          {['cosa-tp', 'maury-b', 'video-storia', 'loghi-tp', 'born-mlist-mirc']
            .map((id) => sections.find((s) => s.id === id))
            .filter(Boolean)
            .map((section) => (
              <SectionCard key={section.id} section={section} />
            ))}
        </div>

        {/* Modalità Section */}
        <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4" style={{ fontFamily: 'Bebas Neue, sans-serif', letterSpacing: '1px' }}>
          MODALITÀ
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {sections.filter(section => !['cosa-tp', 'loghi-tp', 'maury-b', 'video-storia', 'born-mlist-mirc'].includes(section.id)).map((section) => (
            <SectionCard key={section.id} section={section} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default BiografiePage;
